// Vancouver, "Rain over Coal Harbour". A soft, steady late-October rain seen from the marina floats in Coal Harbour,
// looking east-north-east across the mouth of the harbour: Canada Place's five lit sails on its pier, the hotel tower at
// its landward end, downtown's glass towers and Harbour Centre's saucer, the Convention Centre's green roof and the
// floatplane docks; across Burrard Inlet, North Vancouver's lights at the foot of the North Shore Mountains, Grouse
// Mountain's lit runs, chalet, tram and wind turbine, the Lions veiled in cloud; Stanley Park's dark Brockton Point
// with its lighthouse. The SeaBus crosses to Lonsdale Quay and back, a tug heads out with a barge, a freighter swings
// at anchor, sailboats bob on the floats, a heron fishes from a piling and someone waits under a red umbrella.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll,
  hash, rng, bayer, hex, rgba, makeCanvas, px, rect, makeHalo, drawHalo, reflect, streak, text, textWidth, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Scene constants ================= */
const WL_FAR = 156;                                  // North Vancouver's shore across the inlet
const WL = 192;                                      // waterline along Stanley Park, Canada Place and the Convention Centre
const DOCK = 246;                                    // the near edge of our float
const CP = { x0: 266, x1: 350, roof: 165, deck: 180, hall: 273 };   // Canada Place's pier, the prow (north end) on the left
const SAILS = [[280, 145], [295, 143], [310, 142], [325, 143], [340, 145]];   // the five sails' peaks (x, y)
const SAIL_BASE = 165, SAIL_VALLEY = 161, SAIL_L = 272, SAIL_R = 349;
const SAIL_GAP = SAILS[1][0] - SAILS[0][0], SAIL_SAG = 1.7, MAST = 4;   // the fabric's catenary sag; the masts above the peaks
const HOTEL = { x0: 331, x1: 349, top: 110 };        // the hotel tower at the landward (south) end of the pier
const BB = { x: 352, y: 126, w: 44, h: 22 };         // the billboard's face, on a rooftop scaffold beside Canada Place
const BLD = { x0: 350, x1: 401, roof: 151 };         // the mid-rise it stands on
const HC = { cx: 452, x0: 446, x1: 458, roof: 63, disc: 47, top: 31 };   // Harbour Centre: the office slab, its saucer, the antenna tip
const CCW = { x0: 352, roofL: 176, roofR: 170 };     // the Convention Centre's west building, its green roof
const FP_WL = 204;                                   // the floatplane docks in front of it
const SP_TIP = 152;                                  // Brockton Point, the tip of Stanley Park
const LH = { x: 145, y: 174 };                       // its lighthouse (lantern)
const GROUSE = { x: 118, y: 60 };                    // Grouse Mountain's summit (the turbine)
const CHALET = { x: 127, y: 75 };
const TRAM_BASE = { x: 97, y: 128 };                 // the aerial tram's valley station
const LONSDALE = { x: 229, quay: 238 };              // Lonsdale Avenue climbing the hill, the Quay's SeaBus berth
const LAMP = { x: 196, top: 212 };                   // the float's lamp post
const PILE = { x: 152, top: 223, wl: 234 };          // the heron's piling
const UMB = { x: 262, y: DOCK + 3 };                 // someone under a red umbrella at the float's edge (feet)
const BOATS = [                                      // sailboats on the floats: hull x span, waterline, mast x/top, bow dir
  { x0: 4, x1: 98, wl: 238, mast: 50, top: 52, dir: 1, k: 11, ph: 0.1, seed: 3 },
  { x0: 400, x1: 474, wl: 237, mast: 434, top: 72, dir: -1, k: 13, ph: 0.55, seed: 4 },
];

/* ================= Colour + pixel helpers ================= */
const RGB = {};
const rgbOf = h => RGB[h] || (RGB[h] = hex(h));
/** ordered-dither pick from a palette (v in 0..1) */
function dpick(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
/** ImageData-backed painter for prerendering (hex colours) */
function painter(w = W, h = H) {
  const [c, x] = makeCanvas(w, h);
  const img = x.createImageData(w, h), d = img.data;
  const p = { c, x, w, h };
  p.set = (i, y, col) => {
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
  };
  p.seta = (i, y, col, a) => {                       // with alpha (0..1)
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = Math.round(a * 255);
  };
  p.has = (i, y) => i >= 0 && y >= 0 && i < w && y < h && d[(y * w + i) * 4 + 3] > 0;
  p.rect = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) p.set(i, y, col); };
  p.line = (x0, y0, x1, y1, col) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let k = 0; k <= n; k++) p.set(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), col);
  };
  p.done = () => { x.putImageData(img, 0, 0); return c; };
  return p;
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length));
  const p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** per-frame pixel line (masts, stays, cables, necks) */
function pline(x0, y0, x1, y1, col) {
  ctx.fillStyle = col;
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), 1, 1);
}
/** a smooth ridge through control points [[x, y], …] (cosine easing between them) */
function ridge(pts, x) {
  if (x <= pts[0][0]) return pts[0][1];
  for (let k = 0; k < pts.length - 1; k++) {
    const [xa, ya] = pts[k], [xb, yb] = pts[k + 1];
    if (x <= xb) { const u = (x - xa) / (xb - xa); return ya + (yb - ya) * (1 - Math.cos(Math.PI * u)) / 2; }
  }
  return pts[pts.length - 1][1];
}
const winOn = (w, salt, t) => hash(w.id + salt, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p;

/* ================= A calm breeze: rain slant, the umbrella, the masts, the wisps ================= */
const breeze = t => clamp(0.32 + 0.16 * wave(t, 4, 0.2) + 0.08 * wave(t, 11, 0.6), 0, 1);

/* ================= Sky: a low overcast deck, lit rose-orange from beneath by the city ================= */
const SKY = ['#080c16', '#0a0f1a', '#0d131f', '#101725', '#141b2b', '#181f31', '#1d2437', '#23293d', '#2a2f43', '#333447',
  '#3d3a4b', '#48404e', '#544650', '#614c52', '#6e5354'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 165), 1.25) * 0.6;
    v += Math.max(0, 1 - Math.hypot((i - 440) / 190, (y - 150) / 120)) * 0.42;      // downtown's glow on the cloud
    v += Math.max(0, 1 - Math.hypot((i - 225) / 170, (y - 160) / 80)) * 0.2;        // North Vancouver's
    v += Math.max(0, 1 - Math.hypot((i - 40) / 130, (y - 150) / 70)) * 0.1;         // West Vancouver's
    v += 0.035 * Math.sin(i * 0.031 + y * 0.12) * Math.sin(i * 0.013 - y * 0.05);   // the cloud deck's texture
    const f = clamp(v, 0, 1) * (SKY.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY[Math.min(k, SKY.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Rain clouds (tileable, slow; bellies lit by the city) ================= */
const CLOUD_DEFS = [
  { seed: 13, y: -10, h: 58, banks: 4, k: 1, pal: { top: '#141a27', body: '#10141f', core: '#0d111b', bot: '#262838', bot2: '#1a1d2a' } },
  { seed: 29, y: 16, h: 58, banks: 3, k: 2, pal: { top: '#1e2230', body: '#1a1e2b', core: '#161a26', bot: '#3e3a48', bot2: '#2a2a38' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 70 + r() * 110, base = h * (0.3 + r() * 0.45);
    const np = 9 + Math.floor(r() * 7);
    for (let i = 0; i < np; i++) {
      const rx = 16 + r() * 30, ry = 3 + r() * 5;                     // flat, soft stratus
      const pb = base + (r() - 0.5) * 8;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: pb - r() * ry * 1.5, rx, ry, base: pb + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.2;
      if (v > best) best = v;
    }
    best += 0.12 * Math.sin(TAU * 9 * x / W + y * 0.7 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.3);
    best -= Math.max(0, (y - (h - 14)) / 14) * 1.6;                   // nothing reaches the layer's bottom edge
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const [c, cx] = makeCanvas(W, h);
  const img = cx.createImageData(W, h), d = img.data, P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    const o = (y * W + x) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  return { c, y: def.y, k: def.k };
}

/* ================= The North Shore Mountains =================
   Back: the Lions' twin granite peaks (far left, first snow) and the ranges behind Grouse, hazy blue. Front: West
   Vancouver's slopes, Grouse Mountain (its summit turbine, chalet, tram and lit runs), Mount Fromme, the Lynn valley
   dip and Mount Seymour, sinking east behind Canada Place. The lower slopes are strewn with street lights. */
const FAR_R = [[0, 92], [5, 86], [10, 77], [13, 79], [17, 88], [21, 86], [25, 79], [28, 81], [33, 90], [48, 94], [66, 86],
  [96, 76], [128, 66], [148, 57], [158, 62], [178, 68], [204, 72], [232, 78], [262, 74], [286, 80], [316, 98], [350, 112], [480, 128]];
const FRONT_R = [[0, 108], [16, 102], [36, 98], [54, 92], [74, 82], [94, 71], [108, 63], [118, 60], [128, 63], [142, 70],
  [158, 77], [172, 80], [188, 76], [202, 81], [218, 91], [236, 98], [250, 95], [266, 90], [284, 86], [300, 91], [322, 104],
  [346, 120], [380, 132], [480, 140]];
const FARM = ['#1e2433', '#232a3a', '#293042', '#30374a', '#384054'];
const FRONTM = ['#0b1019', '#0e141e', '#121925', '#161e2b', '#1b2432', '#212a39', '#283143'];
const SNOW = ['#4a5064', '#5e6478', '#767c90', '#9096a8'];
const SLOPE_COLS = ['#e8a45a', '#d88e4a', '#f2dcb0', '#cfe0f4', '#f6c070'];
/** the front ridge with a ragged treeline */
const frontTop = x => Math.round(ridge(FRONT_R, x) + (hash(x, 0, 77) < 0.3 ? 1 : 0) - (hash(x >> 1, 0, 78) < 0.2 ? 1 : 0));
// the lit runs on Grouse's face, spreading down from below the chalet: [x0, y0, x1, y1, half-width top, bottom]
const RUNS = [[123, 80, 118, 105, 1.2, 2.8], [113, 72, 106, 92, 0.7, 1.5], [132, 80, 139, 97, 0.7, 1.5]];
const RUN_PAL = ['#161c28', '#1c2230', '#232a38', '#2c3444', '#383f50'];
function renderMountains() {
  const p = painter(), r = rng(21), twinkle = [], runLights = [];
  // the far ranges (the Lions' twin granite peaks at the far left hold the first snow)
  for (let x = 0; x < W; x++) {
    const top = Math.round(ridge(FAR_R, x) + (hash(x, 0, 71) - 0.5) * 1.2);
    for (let y = top; y < WL_FAR; y++) {
      const f = (y - top) / 40;
      let col = dpick(FARM, 0.62 - 0.35 * Math.min(1, f) + (x < 34 ? 0.1 : 0), x, y);
      if (x < 34 && y < top + 3 + hash(x, y >> 1, 72) * 5 && hash(x >> 1, y, 73) < 0.8)
        col = dpick(SNOW, 0.7 - (y - top) * 0.12 + ((x + y) % 5 < 2 ? 0.15 : 0), x, y);
      p.set(x, y, col);
    }
  }
  // the front range
  for (let x = 0; x < W; x++) {
    const top = frontTop(x);
    for (let y = top; y < WL_FAR; y++) {
      let v = 0.16 + 0.42 * smooth(top + 12, WL_FAR + 6, y) + (hash(x >> 1, y >> 1, 74) - 0.5) * 0.14;   // forest texture, city glow low down
      if (y === top) v = 0.55; else if (y === top + 1) v = 0.38;                                   // the skyglow rim
      p.set(x, y, dpick(FRONTM, v, x, y));
    }
  }
  // Grouse's lit runs: faint lit swaths down the forest, light towers along their edges
  for (const [x0, y0, x1, y1, w0, w1] of RUNS) {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let k = 0; k <= n; k++) {
      const u = k / n, cx = lerp(x0, x1, u), cy = Math.round(lerp(y0, y1, u)), w = lerp(w0, w1, u);
      for (let dx = -Math.ceil(w); dx <= Math.ceil(w); dx++) {
        const X = Math.round(cx + dx), e = Math.abs(dx) / (w + 0.8);
        if (e > 1) continue;
        p.set(X, cy, dpick(RUN_PAL, 0.9 - e * 0.55 - u * 0.3 + (hash(X, cy, 79) - 0.5) * 0.2, X, cy));
      }
      if (r() < 0.4) runLights.push({ x: Math.round(cx + (r() - 0.5) * 2 * (w + 1)), y: cy, id: runLights.length });
    }
  }
  // the tram's cable, from the valley station up to the chalet
  for (let k = 0; k <= 60; k += 2) {
    const u = k / 60;
    p.set(Math.round(lerp(TRAM_BASE.x, CHALET.x - 3, u)), Math.round(lerp(TRAM_BASE.y, CHALET.y + 2, u)), '#262e3c');
  }
  p.rect(TRAM_BASE.x - 2, TRAM_BASE.y, 5, 3, '#2c323e'); p.set(TRAM_BASE.x, TRAM_BASE.y + 1, '#ffd08a');
  // the chalet below the summit, and the turbine's slim tower on top
  p.rect(CHALET.x - 4, CHALET.y - 1, 9, 3, '#20262f'); p.rect(CHALET.x - 5, CHALET.y - 2, 11, 1, '#343c4a');
  for (let i = -3; i <= 3; i += 2) p.set(CHALET.x + i, CHALET.y, '#ffcf88');
  p.set(CHALET.x - 2, CHALET.y + 1, '#c08850'); p.set(CHALET.x + 2, CHALET.y + 1, '#c08850');
  for (let y = GROUSE.y - 5; y < GROUSE.y; y++) p.set(GROUSE.x, y, '#3a4252');
  // street lights across the lower slopes: West Vancouver climbs higher (left), North Vancouver below Grouse and Fromme
  for (let n = 0; n < 900; n++) {
    const x = Math.floor(r() * 330), u = Math.pow(r(), 0.7);
    const limit = x < 90 ? 106 : x < 150 ? 118 : x < 260 ? 114 : 120;
    const y = Math.round(lerp(limit, WL_FAR - 2, u) + 1.5 * Math.sin(x * 0.09 + u * 7));
    if (y < frontTop(x) + 8) continue;
    if (x > 104 && x < 150 && y < 112) continue;                    // Grouse's upper slopes stay dark
    const q = r(), col = SLOPE_COLS[Math.floor(r() * SLOPE_COLS.length)];
    if (q < 0.12) twinkle.push({ x, y, col, id: twinkle.length });
    else p.set(x, y, u < 0.4 && q < 0.6 ? dpick(['#5a4a3c', '#8a6a4a'], r(), x, y) : col);
  }
  // Lonsdale Avenue: a straight line of lights up the hill, and Capilano Road up to the tram
  for (let y = 112; y < WL_FAR - 2; y += 2) p.set(LONSDALE.x + Math.round((WL_FAR - y) * 0.03), y, y % 4 ? '#f2c078' : '#ffe0a8');
  for (let k = 0; k < 14; k++) p.set(Math.round(TRAM_BASE.x + 6 - k * 0.5), TRAM_BASE.y + 4 + k * 2, '#e8a45a');
  return { c: p.done(), twinkle, runLights };
}

/* low cloud wisps drifting across the slopes (tileable, dithered alpha) */
function renderWisp(seed, h, dens) {
  const p = painter(W, h), r = rng(seed), blobs = [];
  for (let i = 0; i < 5; i++) blobs.push({ x: r() * W, y: h * (0.4 + r() * 0.25), rx: 24 + r() * 50, ry: 2.5 + r() * h * 0.18 });
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    let v = 0;
    for (const b of blobs) {
      let dx = i - b.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    }
    v += 0.22 * Math.sin(TAU * 7 * i / W + y * 0.6 + seed) * Math.sin(TAU * 13 * i / W - y * 0.25) - 0.08;
    if (v <= 0) continue;
    const q = Math.floor(Math.min(1, v * 1.3) * 4 + bayer(i, y)) / 4;   // four dithered levels of alpha, no screen door
    if (q > 0) p.seta(i, y, v > 0.5 ? '#5e5664' : '#4a4854', q * dens);
  }
  return p.done();
}

/* ================= North Vancouver's waterfront: Lonsdale's towers, the Quay, the shore lights ================= */
const WARM_A = '#ffd88e', WARM_B = '#eea45e', COOL_A = '#d4ecff', COOL_B = '#9cc8ec';
const NV_TOWERS = [[204, 209, 132], [211, 216, 126], [219, 223, 136], [233, 239, 124], [241, 246, 130], [250, 254, 138], [262, 267, 134]];
function renderNorthVan() {
  const p = painter(), r = rng(33), wins = [];
  for (const [x0, x1, top] of NV_TOWERS) {                           // Lonsdale's high-rises
    for (let y = top; y < WL_FAR; y++) for (let i = x0; i <= x1; i++)
      p.set(i, y, y === top ? '#3a4254' : i === x0 ? '#262d3c' : i === x1 ? '#141922' : '#1c222e');
    for (let y = top + 2; y < WL_FAR - 6; y += 2) for (let i = x0 + 1; i < x1; i += 2) {
      const q = r();
      if (q < 0.28) p.set(i, y, q < 0.14 ? WARM_A : q < 0.22 ? WARM_B : COOL_B);
      else if (q < 0.36) wins.push({ id: wins.length, x: i, y, n: 2 + Math.floor(r() * 5), p: 0.5, off: r(), col: r() < 0.6 ? WARM_A : COOL_A });
    }
    p.set(x0 + ((x1 - x0) >> 1), top - 1, '#3a4254');
  }
  for (let x = 146; x < 292;) {                                     // low blocks along the shore
    const w = 4 + Math.floor(r() * 9), top = 144 + Math.floor(r() * 8);
    for (let y = top; y < WL_FAR; y++) for (let i = x; i < x + w; i++) if (!p.has(i, y)) p.set(i, y, y === top ? '#2a3140' : '#161b25');
    for (let y = top + 2; y < WL_FAR - 2; y += 2) for (let i = x + 1; i < x + w - 1; i += 2) if (r() < 0.2) p.set(i, y, r() < 0.7 ? WARM_B : COOL_B);
    x += w;
  }
  // Lonsdale Quay: the market hall's lit glass front and the SeaBus berth's canopy
  for (let y = 149; y < WL_FAR; y++) for (let i = 214; i < 238; i++) {
    let col = y === 149 ? '#8a8490' : y === 150 ? '#4a4450' : (i % 3 === 0 ? '#3a3440' : y < 153 ? '#ffd89a' : '#e8a864');
    if (y >= 154) col = '#2a2630';
    p.set(i, y, col);
  }
  for (let i = 238; i < 248; i++) { p.set(i, 151, '#c8ccd8'); p.set(i, 152, '#6a7080'); if (i % 3 === 0) for (let y = 153; y < WL_FAR; y++) p.set(i, y, '#3a3e4a'); }
  for (let i = 146; i < 292; i += 3) if (r() < 0.7) p.set(i, WL_FAR - 1, r() < 0.5 ? '#f2c078' : '#fff0d0');   // the Esplanade's lights
  return { c: p.done(), wins };
}

/* ================= Stanley Park: Brockton Point's firs, its seawall and the little lighthouse ================= */
const SP_LAMPS = [10, 33, 62, 85, 111, 131];
function renderStanley() {
  const p = painter(), r = rng(44);
  const baseTop = x => x < 116 ? lerp(150, 158, x / 116) : lerp(158, 186, Math.pow((x - 116) / (SP_TIP - 116), 1.3));
  const tops = new Float32Array(SP_TIP + 2).fill(999), lit = new Uint8Array(SP_TIP + 2);
  for (let cx = -4; cx < SP_TIP; cx += 3 + Math.floor(r() * 4)) {    // pointed crowns of Douglas fir and cedar
    const h = 3 + r() * (cx < 120 ? 7 : 4), hw = 2 + r() * 2.5;
    for (let x = Math.floor(cx - hw); x <= Math.ceil(cx + hw); x++) {
      if (x < 0 || x > SP_TIP) continue;
      const y = baseTop(cx) - h + Math.abs(x - cx) * (h / hw);
      if (y < tops[x]) { tops[x] = y; lit[x] = x > cx ? 1 : 0; }
    }
  }
  for (let x = 0; x <= SP_TIP; x++) {
    const top = Math.round(Math.min(tops[x], baseTop(x) + 2));
    for (let y = top; y < 186; y++) {
      let v = 0.3 + 0.25 * smooth(top, 186, y) + (hash(x >> 1, y >> 1, 45) - 0.5) * 0.3;
      if (y - top < 2) v += lit[x] ? 0.4 : 0.15;                    // crowns lit from the city side
      p.set(x, y, dpick(['#070d0e', '#0a1212', '#0e1717', '#121d1c', '#182622', '#20302b'], v, x, y));
    }
  }
  // the seawall and its path, curving round the point
  for (let x = 0; x <= SP_TIP + 2; x++) {
    const y0 = x < 136 ? 186 : Math.round(186 + (x - 136) * 0.12);
    p.set(x, y0 - 1, '#3a424c'); p.set(x, y0, '#2a3038');
    for (let y = y0 + 1; y < WL; y++) p.set(x, y, (x + (y & 1) * 3) % 6 === 0 ? '#12161c' : y === WL - 1 ? '#101318' : '#1c2128');
  }
  for (const lx of SP_LAMPS) { p.set(lx, 183, '#2a3038'); p.set(lx, 184, '#2a3038'); p.set(lx, 182, '#ffe0a8'); }
  // Brockton Point lighthouse: a square white tower on an arch over the path, a red cap on its lantern
  const { x, y } = LH;
  for (let yy = y + 2; yy < 188; yy++) for (let i = x - 2; i <= x + 2; i++) {
    let col = i === x - 2 ? '#e8ecf2' : i === x + 2 ? '#8a90a0' : '#c4cad6';
    if (yy >= 184 && i > x - 2 && i < x + 2) col = '#141820';        // the arch
    if (yy === y + 6) col = '#b03a36';
    p.set(i, yy, col);
  }
  p.rect(x - 1, y - 1, 3, 3, '#2a2e36');                             // lantern glazing (lit per frame)
  p.rect(x - 2, y - 2, 5, 1, '#c0302c'); p.set(x, y - 3, '#c0302c');
  return { c: p.done(), tops: Array.from(tops, (v, i) => Math.round(Math.min(v, baseTop(i) + 2))) };
}

/* ================= Canada Place: the hotel tower, the pier building with its promenade, then five sails =================
   Seen side-on from the west: the prow (the pier's north end) on the left, the five white fabric sails in a row along
   the roof, the hotel's tower rising behind the last of them at the landward end. Each sail is a tent of white
   Teflon-coated fibreglass pulled up to a point by its mast, its sides sagging in catenaries deep down between the peaks.
   They are floodlit white; twice a loop a light show runs colours along the row. Each sail is pre-rendered in each
   light and cross-faded. */
const CP_LAMPS = Array.from({ length: 13 }, (_, i) => CP.x0 + 6 + i * 6);
const CP_FLAGS = [CP.x0 + 2, CP.x0 + 5, CP.x0 + 8];
function renderCanadaPlace() {
  const p = painter(), r = rng(55), wins = [];
  // the hotel tower: a curved slab, lit from the left, warm rooms
  for (let y = HOTEL.top; y < CP.roof; y++) for (let i = HOTEL.x0; i <= HOTEL.x1; i++) {
    const u = (i - HOTEL.x0) / (HOTEL.x1 - HOTEL.x0);
    let v = 0.72 - 0.55 * u + 0.08 * Math.sin(u * Math.PI);
    if (y < HOTEL.top + 3) v = y === HOTEL.top ? 0.95 : 0.2;
    p.set(i, y, dpick(['#26262e', '#36343e', '#4a4650', '#625c64', '#7c7478', '#9e9492', '#d8ccc4'], v, i, y));
  }
  for (let y = HOTEL.top + 4; y < CP.roof - 1; y += 3) for (let i = HOTEL.x0 + 1; i < HOTEL.x1; i += 2) {
    const q = r();
    if (q < 0.3) p.set(i, y, q < 0.18 ? WARM_A : WARM_B);
    else if (q < 0.42) wins.push({ id: wins.length, x: i, y, n: 2 + Math.floor(r() * 5), p: 0.5, off: r() });
    else p.set(i, y, '#1a1e2a');
  }
  // the pier building
  const bay = CP.x0 + 12;                                             // the terminal's first bay
  for (let x = CP.x0; x <= CP.x1; x++) {
    const prow = x < CP.hall, dx = x - CP.x0;
    for (let y = CP.roof; y < WL; y++) {
      let col = null;
      if (y < CP.deck - 2) {                                          // the upper hall (none over the prow)
        if (prow) continue;
        if (y <= CP.roof + 1) col = y === CP.roof ? '#a8aec0' : '#6a7080';
        else if (y <= CP.roof + 4) col = x % 4 === 0 ? '#5a6070' : y === CP.roof + 2 ? '#fff0c8' : '#f0c888';
        else if (y === CP.roof + 8) col = '#7a8092';
        else if (y === CP.roof + 11) col = '#4a5062';
        else col = dpick(['#7a8092', '#9098aa', '#aab0c2', '#c4cad8'], 0.9 - 0.55 * (x - CP.hall) / (CP.x1 - CP.hall) - (y > CP.roof + 8 ? 0.2 : 0), x, y);
        if (y === CP.roof + 6 && x % 3 === 1 && x > CP.hall + 2) col = r() < 0.5 ? '#dcecff' : '#3a4050';
        if (y === CP.roof + 10 && x % 4 === 2 && x > CP.hall + 2) col = r() < 0.35 ? '#ffe0a8' : '#2e3444';
      } else if (y === CP.deck - 2) col = prow && dx < 3 ? null : '#1e222c';
      else if (y <= CP.deck) {                                       // the promenade, tapering to the prow
        if (dx < (CP.deck - y) * 2) continue;
        col = y === CP.deck - 1 ? (x % 2 ? '#9aa0b0' : '#5a6070') : '#464c5a';
      } else {                                                        // the terminal's lower wall and the pilings
        if (dx < (y - CP.deck) * 0.7) continue;
        col = '#343a48';
        if (y >= WL - 3) col = (x % 3) ? '#12161e' : '#1e2430';
        else if (y > CP.deck + 1 && y < WL - 4 && x > bay && (x - bay) % 14 < 8) col = (x - bay) % 14 === 0 ? '#2a303c' : (hash(Math.floor((x - bay) / 14), 0, 57) < 0.35 ? (y < CP.deck + 4 ? '#e8c890' : '#b08a58') : '#161a24');
        if (y > CP.deck && y < WL - 3 && (x - bay + 4) % 14 === 13) col = '#222630';   // fenders
      }
      if (col) p.set(x, y, col);
    }
  }
  for (const fx of CP_FLAGS) for (let y = CP.deck - 8; y < CP.deck - 1; y++) p.set(fx, y, '#7a8090');   // flagpoles on the prow
  for (const lx of CP_LAMPS) { p.set(lx, CP.deck - 3, '#fff0c8'); p.set(lx, CP.deck - 2, '#3a3e48'); }
  return { c: p.done(), wins };
}
// the sails' lights, each shadow to highlight: floodlit white (0), and the light shows' washes of colour
const SAIL_PALS = [
  ['#464b60', '#6c7189', '#989db4', '#c2c6d6', '#e2e5ee', '#ffffff'],   // white
  ['#3c0e18', '#6e1a28', '#b02a3c', '#e44a5a', '#ff8a92', '#ffd6d8'],   // red
  ['#44200e', '#7a3a14', '#c0621e', '#f08c36', '#ffbc74', '#ffe8cc'],   // orange
  ['#0e3822', '#176034', '#249a52', '#3ccc78', '#88eab0', '#dcfbe8'],   // green
  ['#101e4c', '#1c3584', '#2c58c4', '#4c86f0', '#8cb6ff', '#dce8ff'],   // blue
  ['#2a1046', '#4c1c7a', '#7634b2', '#a05ade', '#c89cf4', '#efe0ff'],   // violet
];
/** the top edge of sail k at column x (concave tent sides sagging into the valleys between the peaks) */
function sailSpan(k) {
  const [px0] = SAILS[k];
  const L = k === 0 ? SAIL_L : (SAILS[k - 1][0] + px0) / 2, R = k === SAILS.length - 1 ? SAIL_R : (SAILS[k + 1][0] + px0) / 2;
  return [Math.floor(L), Math.ceil(R)];
}
function sailTop(k, x) {
  const [px0, py] = SAILS[k], [L, R] = sailSpan(k);
  const edge = x < px0 ? (k === 0 ? SAIL_BASE : SAIL_VALLEY) : (k === SAILS.length - 1 ? SAIL_BASE : SAIL_VALLEY);
  const u = x < px0 ? (px0 - x) / (px0 - L) : (x - px0) / (R - px0);
  return Math.round(edge - (edge - py) * (1 - Math.min(1, u)) ** SAIL_SAG);
}
function renderSail(k, pal) {
  const [px0, py] = SAILS[k], [L, R] = sailSpan(k), w = R - L + 1, h = SAIL_BASE - py + MAST, oy = py - MAST;
  const p = painter(w, h);
  for (let x = L; x <= R; x++) {
    const top = sailTop(k, x);
    for (let y = top; y < SAIL_BASE; y++) {
      const f = (y - top) / Math.max(1, SAIL_BASE - top), far = x > px0, side = far ? -0.16 : x < px0 ? 0.08 : 0;
      let v = 0.48 + 0.34 * f + side;                                     // floodlit from the roof below, the far facet in shade
      const seam = frac((x - px0) / (y - py + 2) * 2.2 + 0.5);            // fabric panels radiating from the peak
      if (seam < 0.14 && y - top > 1) v -= 0.16;
      if (y - top < 1) v = far ? 0.84 : 0.98; else if (y - top < 2) v = far ? 0.64 : 0.82;   // the lit edge
      if (y - py < 3 && Math.abs(x - px0) < 2) v = Math.max(v, 0.9);      // the bright peak
      p.set(x - L, y - oy, dpick(pal, v, x, y));
    }
  }
  for (let y = py - MAST; y < py; y++) p.set(px0 - L, y - oy, y === py - MAST ? '#b4b8c4' : '#787c8a');   // the mast
  return { c: p.done(), x: L, y: oy };
}

/* ================= Downtown behind: glass towers, Harbour Centre, the billboard's block, the Convention Centre ================= */
//             x0   x1  top kind
const TOWERS = [[352, 366, 100, 'glass'], [366, 380, 116, 'box'], [380, 396, 92, 'glass'], [396, 410, 84, 'crown'], [410, 422, 58, 'glass'],
  [422, 436, 98, 'box'], [436, 447, 80, 'glass'], [458, 471, 72, 'deco'], [471, 480, 90, 'glass']];
const GLASS = ['#131a28', '#18212f', '#1e2838', '#253042', '#2e3a4e', '#3a485e'];
const CONC = ['#20222c', '#2a2c36', '#363842', '#44464f', '#54565f'];
const DECO = ['#2a2224', '#382c2c', '#4a3a36', '#5e4a42', '#76604e', '#92785e'];
const HC_CONC = ['#3e4048', '#52545c', '#686a72', '#7e8088', '#96989e', '#acaeb4'];
function renderDowntown() {
  const p = painter(), r = rng(66), wins = [];
  for (const [x0, x1, top, kind] of TOWERS) {
    const w = x1 - x0;
    for (let y = top; y < CCW.roofR + 8; y++) for (let i = x0; i < x1; i++) {
      const u = (i - x0) / Math.max(1, w - 1);
      let col;
      if (kind === 'deco') {                                          // setbacks up to a lantern, floodlit terracotta
        const hw = y < top + 4 ? 2 : y < top + 10 ? 4 : 7, c = x0 + 6;
        if (Math.abs(i - c) > hw) continue;
        col = dpick(DECO, 0.75 - 0.5 * (i - c + hw) / (2 * hw) + (y < top + 12 ? 0.15 : 0) - (y - top) * 0.004, i, y);
        if (y === top + 4 || y === top + 10) col = DECO[5];
      } else if (kind === 'box') {
        col = dpick(CONC, 0.7 - 0.5 * u, i, y);
        if (y === top) col = CONC[4];
      } else {
        let v = 0.55 - 0.35 * u + 0.25 * Math.max(0, 1 - (y - top) / 50);
        if ((y - top) % 3 === 0) v -= 0.18;
        col = dpick(GLASS, v, i, y);
        if (y === top) col = '#4a5a72';
        if (kind === 'crown' && y < top + 4) col = y === top + 1 ? '#bfe6ff' : '#5a7896';   // a lit crown
      }
      p.set(i, y, col);
    }
    const dy = kind === 'box' ? 3 : kind === 'deco' ? 3 : 3;             // lit windows and offices
    for (let y = top + 3; y < CCW.roofR + 4; y += dy) for (let i = x0 + 1; i < x1 - 1; i += 2) {
      if (kind === 'deco' && Math.abs(i - (x0 + 6)) > (y < top + 10 ? 3 : 6)) continue;
      const q = r();
      const lit = kind === 'glass' || kind === 'crown' ? (r() < 0.55 ? COOL_B : WARM_B) : WARM_B;
      if (q < 0.26) { p.set(i, y, lit); if (kind !== 'deco') p.set(i + 1, y, q < 0.12 ? lit : GLASS[3]); }
      else if (q < 0.36) wins.push({ id: wins.length, x: i, y, n: 2 + Math.floor(r() * 5), p: 0.5, off: r(), col: lit });
    }
    if (kind === 'crown') for (let i = x0; i < x1; i++) p.set(i, top - 1, '#8ab8e0');
  }
  // Harbour Centre: the office slab (pale concrete spandrels between dark window bands, the glass lifts' track up its
  // face), the short neck, the saucer of the lookout and its revolving restaurant, the antenna
  const cx = HC.cx;
  for (let y = HC.roof; y < CCW.roofR + 6; y++) for (let i = HC.x0; i <= HC.x1; i++) {
    const u = (i - HC.x0) / (HC.x1 - HC.x0), band = (y - HC.roof) % 4;
    let col;
    if (y === HC.roof) col = i === HC.x1 ? '#6a6c76' : '#b4b6be';     // the roof's parapet
    else if (band < 2) col = dpick(HC_CONC, 0.84 - 0.72 * u - (band === 1 ? 0.15 : 0), i, y);
    else col = i === HC.x1 ? '#10131a' : band === 2 ? '#1c212c' : '#151922';   // a band of office windows
    if (i === cx - 2 && y > HC.roof) col = band < 2 ? '#3a4656' : '#22303e';   // the lift track
    p.set(i, y, col);
  }
  for (let y = HC.roof + 2; y < CCW.roofR + 4; y += 4) for (let i = HC.x0 + 1; i < HC.x1; i++) {
    if (i === cx - 2) continue;
    const q = r();
    if (q < 0.3) { p.set(i, y, q < 0.18 ? WARM_A : COOL_A); p.set(i, y + 1, q < 0.18 ? WARM_B : COOL_B); }
    else if (q < 0.4) wins.push({ id: wins.length, x: i, y, n: 2 + Math.floor(r() * 5), p: 0.5, off: r(), col: WARM_A });
  }
  for (let y = HC.disc + 6; y < HC.roof; y++) for (let i = cx - 2; i <= cx + 2; i++)   // the neck, the lift track up its side
    p.set(i, y, i === cx - 2 ? '#2a3644' : dpick(HC_CONC, 0.8 - 0.6 * (i - cx + 1) / 3, i, y));
  const DISC = [[HC.disc, 5], [HC.disc + 1, 8], [HC.disc + 2, 9], [HC.disc + 3, 9], [HC.disc + 4, 8], [HC.disc + 5, 5]];
  for (const [y, hw] of DISC) for (let i = cx - hw; i <= cx + hw; i++) {
    let col = dpick(['#3a3c46', '#5a5c66', '#7e808a', '#a4a6b0'], 0.8 - 0.6 * (i - cx + hw) / (2 * hw), i, y);
    if (y === HC.disc + 2) col = (i % 2) ? '#ffe2a4' : '#f0b870';       // the restaurant ring (per-frame lights on top)
    if (y === HC.disc + 3) col = (i % 3) ? '#bcd8f0' : '#6a88a4';       // the lookout's glass
    p.set(i, y, col);
  }
  for (let y = HC.top + 1; y < HC.disc; y++) p.set(cx, y, y > HC.disc - 4 ? '#5a5c66' : '#40424c');
  p.set(cx - 1, HC.disc - 3, '#40424c'); p.set(cx + 1, HC.disc - 3, '#40424c');
  // the billboard's block: a 1960s mid-rise with a rooftop scaffold (the scaffold and sign are drawn per frame)
  for (let y = BLD.roof; y < CCW.roofL + 2; y++) for (let i = BLD.x0; i <= BLD.x1; i++) {
    let col = dpick(CONC, 0.62 - 0.4 * (i - BLD.x0) / (BLD.x1 - BLD.x0), i, y);
    if (y === BLD.roof) col = '#5a5c66';
    else if ((y - BLD.roof) % 4 === 2 && i % 3 !== 0 && i > BLD.x0 && i < BLD.x1) {
      const q = r();
      col = q < 0.3 ? (q < 0.15 ? WARM_A : WARM_B) : '#15171e';
      if (q > 0.3 && q < 0.4) wins.push({ id: wins.length, x: i, y, n: 2 + Math.floor(r() * 5), p: 0.5, off: r(), col: WARM_B });
    }
    p.set(i, y, col);
  }
  p.rect(BLD.x1 - 5, BLD.roof - 4, 5, 4, CONC[1]); p.rect(BLD.x1 - 5, BLD.roof - 4, 5, 1, CONC[3]);   // lift penthouse
  // the Convention Centre's west building: the living roof and the lit glass hall under it, the seawall along it
  for (let x = CCW.x0; x < W; x++) {
    const top = Math.round(lerp(CCW.roofL, CCW.roofR, (x - CCW.x0) / (W - CCW.x0)));
    for (let y = top; y < WL; y++) {
      let col;
      if (y === top) col = '#46603e';
      else if (y < top + 3) col = dpick(['#16221a', '#1c2a1e', '#243424', '#2c3e2a'], 0.5 + (hash(x >> 1, y, 67) - 0.5) * 0.7, x, y);
      else if (y === top + 3) col = '#6a6e78';
      else if (y < WL - 3) {
        const pan = Math.floor((x - CCW.x0) / 4), gy = y - top - 4;
        col = (x - CCW.x0) % 4 === 0 || gy === 4 ? '#262c3a' : hash(pan, gy > 4 ? 1 : 0, 68) < 0.5 ? (gy < 2 ? '#d8bc8c' : gy > 4 ? '#8a6a46' : '#b09064') : '#161a26';
        if (x < CCW.x0 + 3) col = '#8a90a0';
      } else col = y === WL - 3 ? '#8a90a0' : y === WL - 1 ? '#1a1e26' : '#4a5060';
      p.set(x, y, col);
    }
  }
  for (let x = CCW.x0 + 8; x < W; x += 16) { p.set(x, WL - 5, '#fff0c8'); p.set(x, WL - 4, '#2a2e38'); }
  return { c: p.done(), wins };
}

/* ================= The billboard: a steel scaffold on the mid-rise's roof, three gooseneck lamps over the face =================
   BBC is the structure's own canvas (frame, legs, catwalk, lamp arms); the face is painted per frame. */
const BBC = { x: BB.x - 4, y: BB.y - 7, w: BB.w + 8, h: BLD.roof - BB.y + 7 };
const BB_LAMPS = [BB.x + 8, BB.x + 22, BB.x + 36];
const BB_LAMP_Y = BB.y - 5;                          // their lit lenses, facing down at the face
const STEEL = { hi: '#6a7080', mid: '#3e4452', dk: '#262a34', vdk: '#16181f' };
function renderBillboard() {
  const p = painter(BBC.w, BBC.h), set = (i, y, col) => p.set(i - BBC.x, y - BBC.y, col);
  const hl = (x0, y, x1, col) => { for (let i = x0; i <= x1; i++) set(i, y, col); };
  const vl = (x, y0, y1, col) => { for (let y = y0; y <= y1; y++) set(x, y, col); };
  const L = BB.x - 1, R = BB.x + BB.w, T = BB.y - 1, B = BB.y + BB.h;
  hl(L, T, R, STEEL.hi); vl(L, T + 1, B, STEEL.mid); vl(R, T + 1, B, STEEL.vdk); hl(L, B, R, STEEL.dk);
  for (let y = BB.y; y < B; y++) hl(BB.x, y, R - 1, '#0d0f24');
  // the catwalk and the legs down to the roof, X-braced
  const CW = B + 1;
  hl(L - 2, CW, R + 2, STEEL.mid); for (let i = L - 2; i <= R + 2; i += 2) set(i, CW, STEEL.hi);
  for (const lx of [BB.x + 3, BB.x + 21, BB.x + 39]) {
    vl(lx, CW + 1, BLD.roof - 1, STEEL.dk); vl(lx + 1, CW + 1, BLD.roof - 1, STEEL.vdk);
  }
  for (let k = 0; k < BLD.roof - CW - 1; k++) { set(BB.x + 4 + k * 3, CW + 1 + k, STEEL.vdk); set(BB.x + 20 - k * 3, CW + 1 + k, STEEL.vdk); set(BB.x + 22 + k * 3, CW + 1 + k, STEEL.vdk); set(BB.x + 38 - k * 3, CW + 1 + k, STEEL.vdk); }
  // gooseneck lamps: a post up from the top rail, an arm bent out over the face, the hooded head
  for (const lx of BB_LAMPS) {
    vl(lx, BB_LAMP_Y + 1, T - 1, STEEL.dk);
    hl(lx - 2, BB_LAMP_Y - 1, lx + 2, STEEL.vdk); hl(lx - 1, BB_LAMP_Y - 2, lx + 1, STEEL.dk);
    hl(lx - 2, BB_LAMP_Y, lx + 2, '#ffe6b0'); set(lx, BB_LAMP_Y, '#fffaf0');
  }
  return p.done();
}
/* the city's own ad: SAL'S DOCK smoked salmon, a sockeye leaping over the waves, lit from the gooseneck lamps above */
const POSTER_BG = ['#0e2630', '#123240', '#18404e', '#1e505c', '#28646c', '#347a7e'];
const BAND = ['#4a1016', '#661820', '#80222a'];
function renderPoster(tailUp, dim) {
  const { w, h } = BB, p = painter(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    let cone = 0;
    BB_LAMPS.forEach((lx, k) => {
      const dx = (i - (lx - BB.x)) / (5 + j * 0.8);
      cone = Math.max(cone, (k === 2 && dim ? 0.25 : 1) * Math.exp(-dx * dx) * Math.max(0, 1 - j / 18));
    });
    const v = 0.22 + 0.28 * smooth(0.12, 0.4, cone) + 0.3 * smooth(0.5, 0.85, cone) - 0.08 * j / h;
    p.set(i, j, j >= 15 ? dpick(BAND, 0.5 + 0.4 * cone, i, j) : j === 14 ? '#e84a4a' : dpick(POSTER_BG, v, i, j));
  }
  // the salmon: an arched sockeye, green head at the left, red body, pale belly, tail flicking
  for (let k = 0; k <= 12; k++) {
    const x = 2 + k, cy = 7 - Math.round(3.2 * Math.sin(Math.PI * k / 12)), th = k < 2 || k > 10 ? 1 : k < 4 || k > 8 ? 2 : 3;
    for (let d = 0; d < th; d++) p.set(x, cy + d, k < 3 ? (d === 0 ? '#3e6a34' : '#5a8a44') : d === 0 ? '#8e1a24' : d === th - 1 ? '#f4b0a8' : '#e0343c');
  }
  p.set(3, 6, '#ffffff'); p.set(2, 8, '#d8d0c0');                     // eye and the hooked jaw
  const tx = 15, ty = 6;                                              // the tail fin
  if (tailUp) { p.set(tx, ty - 2, '#c82a34'); p.set(tx + 1, ty - 3, '#c82a34'); p.set(tx, ty + 1, '#c82a34'); p.set(tx + 1, ty + 1, '#8e1a24'); }
  else { p.set(tx, ty - 1, '#c82a34'); p.set(tx + 1, ty - 1, '#8e1a24'); p.set(tx, ty + 2, '#c82a34'); p.set(tx + 1, ty + 3, '#c82a34'); }
  p.set(7, 3, '#8e1a24'); p.set(8, 2, '#8e1a24');                     // the dorsal fin
  for (let i = 1; i < 17; i++) {                                      // the waves it leaps from
    p.set(i, 11 + (i % 4 === 1 ? 0 : 1), i % 4 === 1 ? '#bff4f4' : '#5ce1e6');
    if (i % 4 === 3) p.set(i, 12, '#2a8a94');
  }
  const c = p.done(), x = c.getContext('2d');
  text(x, 'SMOKED', 19, 2, '#fbeedc');
  text(x, 'SALMON', 19, 8, '#ff9a8a');
  text(x, "SAL'S DOCK", (w - textWidth("SAL'S DOCK")) >> 1, 16, '#fbeedc');
  return c;
}

/* ================= Water ================= */
function renderWater() {
  const h = DOCK - WL_FAR, P = ['#1a2432', '#162030', '#121b29', '#0f1723', '#0c131d', '#0a1018', '#080d14'];
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) p.set(i, y, dpick(P, Math.pow(y / h, 0.8), i, y));
  return p.done();
}

/* ================= Vessels ================= */
/* the SeaBus: a double-ended catamaran, a band of lit windows, a pilothouse at each end (it never turns round) */
const SEABUS_ROWS = ['....kk..........kk....', '...kGGk........kGGk...', '..wwwwwwwwwwwwwwwwwwww..', '.wLLlLLlLLlLLlLLlLLlLw.',
  '.wLLLLLLLLLLLLLLLLLLLw.', 'wwwwwwwwwwwwwwwwwwwwwwww', 'bbbbbbbbbbbbbbbbbbbbbbbb', '.ddd................ddd.'];
const SEABUS_KEY = { k: '#30343e', G: '#bfe0ff', w: '#d6dae2', L: '#ffe2a0', l: '#a89870', b: '#24406e', d: '#141820' };
/* a harbour tug (bow left) and the gravel barge it tows */
const TUG_ROWS = ['....k.........', '....k.........', '...kkkk.......', '..kGGGGk......', '..wwwwwwww....', '..wLLwwLww....',
  'rrrrrrrrrrrrrr', '.dddddddddddd.', '...ddddddddd..'];
const TUG_KEY = { k: '#2a2e36', G: '#cfe4f8', w: '#c8ccd4', L: '#ffd890', r: '#9a2a24', d: '#1a1416' };
function renderBarge() {
  const w = 34, h = 7, p = painter(w, h);
  for (let i = 3; i < w - 3; i++) {                                  // the gravel heaped on its deck
    const hgt = Math.round(2.2 * Math.sin(Math.PI * (i - 3) / (w - 6)) + (i % 5 === 0 ? 0.5 : 0));
    for (let y = 3 - hgt; y < 3; y++) p.set(i, y, y === 3 - hgt ? '#5a5650' : '#3e3a36');
  }
  for (let i = 0; i < w; i++) { p.set(i, 3, '#4a4a50'); p.set(i, 4, '#26282e'); p.set(i, 5, '#1c1e24'); if (i > 1 && i < w - 2) p.set(i, 6, '#141418'); }
  return p.done();
}
/* a bulk carrier swinging at anchor (bow left): deck cranes, the bridge aft, anchor lights */
function renderFreighter() {
  const w = 52, h = 20, p = painter(w, h), r = rng(77);
  for (let y = 12; y < h; y++) {
    const x0 = Math.max(0, 3 - (y - 12)) + (y > 17 ? 2 : 0), x1 = w - 1 - (y > 16 ? 2 : 0);
    for (let i = x0; i <= x1; i++) p.set(i, y, y === 12 ? '#3a3036' : y >= 18 ? '#4a1e20' : '#1e1a20');
  }
  for (let i = 1; i < w - 1; i += 2) p.set(i, 11, '#2e2c34');
  for (const cx of [9, 19, 29]) {                                    // cranes between the hatches
    for (let y = 5; y < 12; y++) p.set(cx, y, '#3a3a44');
    p.line(cx, 5, cx + 6, 9, '#34343e');
    p.rect(cx + 2, 10, 6, 2, '#2a2830');
  }
  for (let y = 2; y < 12; y++) for (let i = 40; i <= 49; i++)          // the accommodation block and bridge
    p.set(i, y, i === 40 ? '#8a8e98' : i >= 48 ? '#4a4e58' : y === 3 ? '#9aa0aa' : '#6e727c');
  for (const wy of [4, 6, 8, 10]) for (let i = 41; i < 48; i++) p.set(i, wy, (i & 1) ? (r() < 0.6 ? '#ffd48a' : '#2a2e36') : '#5a5e68');
  p.rect(39, 2, 12, 1, '#9aa0aa'); p.rect(45, 0, 1, 2, '#4a4e58');
  return p.done();
}
/* floatplanes moored at the docks (nose left): a high wing, windows, a stripe, floats on struts */
const FP_ROWS = ['.....................yy...', '.wwwwwwwwwwwww......ccc...', '.kddddddddddk.......ccc...', '..kcccccccccccccccccccck..',
  '.kcGGcGGcGGcccccccccccdddd', 'pkcccccccccccccccccck.....', '.kyyyyyyyyyyyyk...........', '..s.......s...............',
  '..s.......s...............', '.ffffffffffffffff.........', 'fFFFFFFFFFFFFFFFFf........'];
const FP_KEY = [
  { k: '#2a2e36', w: '#e8ecf0', d: '#8a909c', c: '#d2d6de', G: '#3a4a60', y: '#e0b030', s: '#3a3e48', p: '#6a7080', f: '#e8e0c8', F: '#8a8270' },
  { k: '#2a2e36', w: '#e8ecf0', d: '#8a909c', c: '#d2d6de', G: '#3a4a60', y: '#2c8a9a', s: '#3a3e48', p: '#6a7080', f: '#e8e0c8', F: '#8a8270' },
];
const PLANES = [{ x: 394, k: 17, ph: 0.2 }, { x: 436, k: 19, ph: 0.7 }];
function renderFloatDock() {
  const p = painter(W - 386, 8);
  for (let i = 0; i < p.w; i++) { p.set(i, 5, (i % 4) ? '#5a544c' : '#3a342e'); p.set(i, 6, '#2a2420'); p.set(i, 7, '#1a1614'); }
  for (const x of [4, 50, 92]) for (let y = 0; y < 7; y++) p.set(x, y, y === 0 ? '#5a5048' : '#2e2622');
  return p.done();
}

/* ================= Sailboats on the floats: hull, cabin, a furled main on its boom (masts and rigging per frame) ================= */
function renderBoat(b) {
  const w = b.x1 - b.x0 + 1, h = 20, p = painter(w, h), r = rng(b.seed * 17), wl = h - 1, f = x => b.dir > 0 ? x : w - 1 - x;
  const sheerY = x => wl - 8 + Math.round(2 * Math.pow(x / (w - 1), 2));        // the sheer rises to the bow
  for (let x = 0; x < w; x++) {
    const bowCut = Math.max(0, x - (w - 10)), sternCut = Math.max(0, 5 - x);
    const top = sheerY(x) - (x > w - 4 ? 1 : 0), bot = wl - Math.round(bowCut * 0.9) - Math.round(sternCut * 0.8);
    for (let y = top; y <= bot; y++) {
      let col = y === top ? '#b8bec8' : y === top + 1 ? '#7a808c' : y > bot - 1 ? '#1a1e26' : '#5e6470';
      if (y === top + 3) col = b.seed === 3 ? '#1e3a6a' : '#6a1e24';                // the boot stripe
      p.set(f(x), y, col);
    }
  }
  const c0 = Math.round(w * 0.32), c1 = Math.round(w * 0.66), ct = wl - 14;   // the cabin with its portholes
  for (let x = c0; x <= c1; x++) for (let y = ct; y < sheerY(x); y++) {
    let col = y === ct ? '#c8ccd4' : '#9aa0aa';
    if (y === ct + 2 && (x - c0) % 5 === 2 && x < c1 - 1) col = r() < 0.6 ? WARM_A : '#2a2e38';
    if (x === c0 || x === c1) col = '#6a707a';
    p.set(f(x), y, col);
  }
  const mx = b.mast - b.x0, boomY = ct - 5;                           // the boom with its sail cover, aft of the mast
  for (let k = 0; k < Math.round(w * 0.36); k++) {
    const x = mx - b.dir * k;
    p.set(x, boomY + 1, '#3a3e48');
    if (k < w * 0.33) { p.set(x, boomY, b.seed === 3 ? '#27345a' : '#2a4a44'); if (k > 2 && k < w * 0.28) p.set(x, boomY - 1, b.seed === 3 ? '#34457a' : '#36605a'); }
  }
  for (let y = boomY + 2; y < ct; y++) p.set(mx, y, '#8a909a');        // the mast's foot
  return { c: p.done(), w, h };
}

/* ================= Creatures and people ================= */
/* a great blue heron: the dagger bill, the black plume streaming back from the white face, the long S of the neck */
const HERON_ROWS = ['...kkk....', '..wwwkkk..', 'bbbwwk....', '....wg....', '...wg.....', '...wg.....', '....wg....', '....wGGG..',
  '...gGGGGG.', '...gGGGGGd', '....GGGGGd', '.....GGdd.', '.....l.l..', '.....l.l..', '.....l.l..', '....ll.ll.'];
const HERON_BODY = HERON_ROWS.map((row, j) => j < 7 ? '.'.repeat(row.length) : row);
const HERON_KEY = { k: '#14161c', w: '#d6dae2', b: '#d8a040', g: '#7a86a0', G: '#5a6884', d: '#344058', l: '#8a7a50' };
const UMB_ROWS = ['.....RRRRR.....', '...RRRRRRRRR...', '.RRRRRRRRRRRRR.', 'RRDRRRDRDRRRDRR', 'D.............D'];
const UMB_KEY = { R: '#b0343c', D: '#6a1a22' };
const PERSON_ROWS = ['.hhh.', 'hhhhh', 'hhhhh', 'ccccc', 'cccccc', 'cccccc', 'cccccc', 'cccccc', 'ccccc', 'ccccc', '.cccc', '.cccc', '.pp.p', '.pp.p', '.pp.p', '.bb.b'];
const PERSON_KEY = { h: '#26303e', c: '#1e2632', p: '#141820', b: '#0c0e12' };
/* gulls: far, mid and near, three flap frames each (x-shaped far ones) */
const GULL_KEY = { w: '#e6eaf2', g: '#8a96a8', k: '#2a2e38', y: '#e8b830', s: '#c4cad4' };
const GULL_ROWS = [
  [['x...x', '.x.x.', '..x..'], ['.x.x.', 'x.x.x'], ['..x..', '.x.x.', 'x...x']],
  [['k.......k', '.g.....g.', '..ww.ww..', '....w....'], ['.gw...wg.', 'k..www..k'], ['...www...', '.ww...ww.', 'k.......k']],
  [['k.............k', '.g...........g.', '.gw.........wg.', '..gw.......wg..', '...ww.....ww...', '....wwwwwwwwwy.', '......ssss.....'],
    ['...............', 'kgg.........ggk', '..ggww...wwgg..', '....wwwwwwwwwy.', '......ssss.....'],
    ['....wwwwwwwwwy.', '...ww.ssss.ww..', '..gw.......wg..', '.gg.........gg.', 'k.............k']],
];
function makeGulls() {
  return GULL_ROWS.map((frames, size) => frames.map(rows => sprite(rows, size === 0 ? { x: '#9aa2b2' } : GULL_KEY)));
}
const PERCH_ROWS = ['..ww.', '.wwwy', 'gww..', 'ggw..', '.y...'], PERCH_KEY = { w: '#dfe3ea', g: '#8a96a8', y: '#d8a830' };
/* buffleheads, the little diving ducks that winter in Coal Harbour: a white patch on the dark head, white sides */
const DUCK_ROWS = ['.kww..', 'kkwk..', '..kkkk', '.wwwwk'], DUCK_KEY = { k: '#1a1e28', w: '#dde2ea' };
const DUCK_HEN = ['.kw...', 'kkk...', '..kkkk', '.ggggk'], HEN_KEY = { k: '#2a2a30', w: '#c8ccd4', g: '#6a6a72' };
const DUCKS = [{ cx: 214, cy: 234, rx: 34, ry: 4, ph: 0.1, hen: false, dive: [150, 157] }, { cx: 222, cy: 236, rx: 30, ry: 3, ph: 0.06, hen: true, dive: [158.5, 164] }];

/* ================= Our float: wet planks, cleats, a dock box, the lamp post, a few fallen maple leaves ================= */
const CLEATS = [74, 330, 452];
const MAPLE = ['#c8322a', '#e0602a', '#a82a26', '#e8902e'];
function renderFloat() {
  const p = painter(), r = rng(88);
  for (let i = 0; i < W; i++) {
    p.set(i, DOCK, r() < 0.15 ? '#8a7a68' : '#6a5e52'); p.set(i, DOCK + 1, '#3a302a'); p.set(i, DOCK + 2, '#221c18');
  }
  const rows = [[DOCK + 3, 2], [DOCK + 5, 3], [DOCK + 8, 3], [DOCK + 11, 4], [DOCK + 15, 4], [DOCK + 19, 5]];
  rows.forEach(([y0, rh], ri) => {
    let x = -Math.floor(r() * 30);
    while (x < W) {
      const len = 26 + Math.floor(r() * 30);
      for (let y = y0; y < Math.min(H, y0 + rh); y++) for (let i = Math.max(0, x); i < Math.min(W, x + len - 1); i++) {
        let col = ['#2a2628', '#2e2a2a', '#26232a'][Math.floor(hash(i >> 3, y0, 89) * 3)];
        if (y === y0) col = '#3a3434';
        if (y === y0 + rh - 1 && rh > 2) col = '#1a1618';
        const sheen = Math.max(0, 1 - Math.hypot((i - LAMP.x) / 60, (y - DOCK - 4) / 10));      // the lamp's light on the wet wood
        if (y <= y0 + 1 && bayer(i, y) < sheen * 1.4) col = y === y0 ? '#8a6a4a' : '#5a4636';
        p.set(i, y, col);
      }
      x += len;
    }
    for (let y = y0; y < y0 + rh; y++) if (y < H) for (let i = 0; i < W; i++) if (!p.has(i, y)) p.set(i, y, '#141214');
    void ri;
  });
  for (let y = DOCK + 3; y < H; y++) for (let i = 0; i < W; i++) if (!p.has(i, y)) p.set(i, y, '#141214');
  for (const cx of CLEATS) { p.rect(cx - 3, DOCK + 3, 7, 1, '#4a4e58'); p.rect(cx - 1, DOCK + 4, 3, 1, '#2a2e36'); p.set(cx - 3, DOCK + 3, '#8a909c'); }
  // a dock box, white fibreglass
  for (let y = DOCK - 4; y < DOCK + 8; y++) for (let i = 404; i < 428; i++)
    p.set(i, y, y === DOCK - 4 ? '#b8bcc4' : y === DOCK - 3 ? '#8a8e98' : i === 404 ? '#7a7e88' : y > DOCK + 5 ? '#26282e' : '#5a5e68');
  // fallen maple leaves stuck to the planks
  for (let n = 0; n < 16; n++) {
    const x = 20 + Math.floor(r() * 440), y = DOCK + 5 + Math.floor(r() * 20), col = MAPLE[Math.floor(r() * MAPLE.length)];
    if (x > 400 && x < 430 && y < DOCK + 8) continue;
    p.set(x, y, col); p.set(x - 1, y, col); p.set(x + 1, y, col); p.set(x, y - 1, col);
    if (r() < 0.5) { p.set(x - 1, y - 1, col); p.set(x + 1, y - 1, col); }
    p.set(x, y + 1, '#5a1a14');
  }
  // the lamp post: plinth, slim shaft, a lantern head
  const I = '#141a1e', IH = '#3a4650';
  for (let y = DOCK + 1; y <= DOCK + 6; y++) { p.rect(LAMP.x - 2, y, 5, 1, I); p.set(LAMP.x - 2, y, IH); }
  for (let y = LAMP.top + 8; y <= DOCK; y++) { p.set(LAMP.x, y, I); p.set(LAMP.x - 1, y, y % 9 === 0 ? IH : I); }
  p.rect(LAMP.x - 3, LAMP.top, 6, 1, I); p.rect(LAMP.x - 2, LAMP.top - 1, 4, 1, I); p.set(LAMP.x - 1, LAMP.top - 2, I);
  for (let y = LAMP.top + 1; y < LAMP.top + 7; y++) { p.set(LAMP.x - 3, y, I); p.set(LAMP.x + 2, y, I); }
  p.rect(LAMP.x - 3, LAMP.top + 7, 6, 1, I);
  return p.done();
}
function renderPile() {
  const h = PILE.wl - PILE.top + 1, p = painter(5, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < 5; i++) {
    let col = i === 0 ? '#6a5a48' : i === 4 ? '#1e1812' : i === 1 ? '#4e4234' : '#3a3026';
    if (y === 0) col = i < 3 ? '#8a7a66' : '#5a4a3a';
    if (y > h - 4) col = i < 2 ? '#2e4a3a' : '#1c2e24';                // weed at the waterline
    p.set(i, y, col);
  }
  return p.done();
}

/* ================= Soft West Coast rain (3 depths), rings on the water and the planks ================= */
// w: 1 warm lamp, 2 cool, 3 the sails (the drop takes the colour of the sail it passes)
const RAIN_LIGHTS = [
  { x: LAMP.x, y: LAMP.top + 4, r: 46, w: 1, near: true }, { x: SAILS[2][0], y: 153, r: 40, w: 3 }, { x: 310, y: 178, r: 18, w: 1 },
  { x: 420, y: 182, r: 34, w: 1 }, { x: 229, y: 150, r: 16, w: 1 }, { x: HC.cx, y: HC.disc + 2, r: 12, w: 1 },
].map(l => ({ ...l, r2: l.r * l.r }));
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BB.x + 22, y: BB.y + 4, r: 26, w: 1, r2: 26 * 26 }];   // + the billboard's lamps
// a picked weather (site/js/engine/weather.js): Vancouver draws its own rain, with its rings on the harbour and the
// planks and the drips off the umbrella; snow and leaves land on the inlet, the harbour and past the bottom of the frame,
// and catch the same lights (the sails white, the lamps warm)
const WEATHER = weatherPlans(['rain']);
const WX_LAND = [[158, 190], [WL + 4, DOCK - 2], [H + 4, H + 4]];
const wxLights = list => list.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: l.w === 3 ? '#e8ecff' : l.w === 2 ? '#d2e8ff' : '#ffd6a0' }));
const WX = { land: WX_LAND, lights: wxLights(RAIN_LIGHTS) };
const WX_BB = { land: WX_LAND, lights: wxLights(RAIN_LIGHTS_BB) };
function litAt(x, y, far, lights) {
  for (const l of lights) { if (far && l.near) continue; const dx = x - l.x, dy = y - l.y; if (dx * dx + dy * dy < l.r2) return l.w; }
  return 0;
}
function nearLand(x, a, b) {
  if (Math.abs(x - UMB.x) <= 7 && a < 0.55) return UMB.y - 22;       // on the umbrella
  if (x >= 404 && x < 428 && a < 0.5) return DOCK - 4;               // on the dock box
  if (a < 0.08) return DOCK;
  return DOCK + 4 + Math.floor(b * (H - DOCK - 6));
}
const RAIN = [
  { count: 190, nMin: 150, nMax: 176, fall: 190, ff: 1, len: 3, far: true, s0: 0.06, s1: 0.1, col: 'rgba(150,166,196,0.28)', warm: 'rgba(255,210,150,0.5)', cool: 'rgba(200,226,255,0.55)', splash: '#8aa0b8',
    land: (x, a) => 158 + Math.floor(a * 32) },
  { count: 140, nMin: 196, nMax: 222, fall: 240, ff: 0.9, len: 5, s0: 0.08, s1: 0.12, col: 'rgba(170,186,214,0.36)', warm: 'rgba(255,214,160,0.62)', cool: 'rgba(210,232,255,0.62)', splash: '#a8bcd0',
    land: (x, a) => WL + 4 + Math.floor(a * (DOCK - WL - 6)) },
  { count: 64, nMin: 250, nMax: 280, fall: 300, ff: 0.86, len: 8, s0: 0.1, s1: 0.14, col: 'rgba(196,210,234,0.46)', warm: 'rgba(255,220,170,0.78)', cool: 'rgba(220,238,255,0.7)', splash: '#c8d8e8',
    land: nearLand },
];
RAIN.forEach((L, li) => {
  const r = rng(200 + li);
  L.drops = []; L.depth = li;
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
const RINGS = [], PUDDLE_RINGS = [];
{ const r = rng(71); for (let i = 0; i < 54; i++) RINGS.push({ id: i, n: 100 + Math.floor(r() * 50), ph: r() }); }
{ const r = rng(72); for (let i = 0; i < 16; i++) PUDDLE_RINGS.push({ id: 60 + i, n: 80 + Math.floor(r() * 30), ph: r() }); }
function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 4; d[o + 1] = 8; d[o + 2] = 14; d[o + 3] = Math.round(q * 0.45 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Timings (loop seconds) and small tables ================= */
// the SeaBus: out from behind Canada Place to Lonsdale Quay, a wait at the berth, then back; one vessel all loop long
const SB = { out: [12, 64], back: [112, 164], ax: 292, awl: 186, bx: 236, bwl: 158 };
const HORNS = [SB.out[0] + 0.6, SB.back[0] + 0.4];
const TUG = { t0: 168, t1: 236, wl: 176, x0: 292, x1: 96 };
const FREIGHTER = { x: 150, wl: 168 };
const NEAR_GULLS = [{ a: 44, b: 50, y0: 58, dip: 20 }, { a: 196, b: 203, y0: 44, dip: 24 }];
const GULL_T = NEAR_GULLS.map(g => +((g.a + g.b) / 2).toFixed(1));
const STRIKE = 86;                                   // the heron's strike (it comes up with a fish)
const CHIME_T = 140;                                 // the steam clock's whistle, drifting over from Gastown
const CYCLIST = { t0: 58, t1: 71, x0: 486, x1: 352, y: WL - 4 };
const JOGGER = { t0: 120, t1: 158, x0: -6, x1: 138, y: 185 };
const PLANE = { t0: 26, t1: 92 };
const LAMP_FLICK = [[76, 77.3], [183.2, 184.6]];
const BB_FLICK = [[96.4, 98.1], [214.2, 215]];
const SP_FLICK = [[150, 152.2]];
// the sails' light shows: white all loop but for these. Each sail steps through `seq` (SAIL_PALS indices), a step every
// `dwell` s that cross-fades over its first `fade` s, each sail `lag` s behind the one on its left, so the colours
// chase along the row from the prow to the hotel and leave it white again
const SAIL_SHOWS = [
  { t0: 98, seq: [0, 1, 2, 3, 4, 5, 0], dwell: 2, fade: 0.8, lag: 1 },     // a rainbow, 98-114 s
  { t0: 204, seq: [0, 1, 0], dwell: 5, fade: 1.6, lag: 0.9 },             // a wash of red, 204-217.6 s
];
const FAR_GULLS = [], MID_GULLS = [];
{
  const r = rng(515);
  for (let i = 0; i < 5; i++) FAR_GULLS.push({ cx: 160 + r() * 100, cy: 112 + r() * 30, rx: 8 + r() * 20, ry: 2 + r() * 6,
    k: (3 + Math.floor(r() * 4)) * (r() < 0.5 ? 1 : -1), ph: r(), kf: 20 + Math.floor(r() * 30), pf: r() });
  for (let i = 0; i < 2; i++) MID_GULLS.push({ cx: 200 + i * 120 + r() * 30, cy: 176 + r() * 16, rx: 10 + r() * 10, ry: 3 + r() * 4,
    k: 3 + Math.floor(r() * 3), ph: r(), kf: 30 + Math.floor(r() * 20), pf: r() });
}
const waterlineAt = x => x >= 150 && x < CP.x0 ? WL_FAR : WL;
const STREAKS = [
  { x: LONSDALE.x, w: 2, col: '#ffd490', a: 0.3, seed: 1, y0: WL_FAR }, { x: 226, w: 6, col: '#ffcf88', a: 0.3, seed: 2, y0: WL_FAR },
  { x: 214, w: 2, col: '#cfe4ff', a: 0.22, seed: 3, y0: WL_FAR }, { x: 244, w: 3, col: '#e8ecf8', a: 0.24, seed: 4, y0: WL_FAR },
  { x: 180, w: 2, col: '#f2c078', a: 0.2, seed: 5, y0: WL_FAR }, { x: 262, w: 2, col: '#f2c078', a: 0.2, seed: 6, y0: WL_FAR },
  ...[158, 170, 192, 204, 252, 270].map((x, i) => ({ x, w: 2, col: i & 1 ? '#e8a45a' : '#f2dcb0', a: 0.16, seed: 70 + i, y0: WL_FAR })),
  ...CP_LAMPS.filter((x, i) => i % 2 === 0).map((x, i) => ({ x, w: 2, col: '#ffe0a0', a: 0.3, seed: 10 + i, y0: WL })),
  ...[372, 400, 428, 456].map((x, i) => ({ x, w: 6, col: '#ffd08a', a: 0.26, seed: 20 + i, y0: WL })),
  ...SP_LAMPS.map((x, i) => ({ x, w: 2, col: '#ffdca0', a: 0.24, seed: 30 + i, y0: WL })),
  { x: HC.cx, w: 3, col: '#ffe0a4', a: 0.16, seed: 40, y0: WL },
];
const NV_STREAKS = STREAKS.filter(s => s.y0 === WL_FAR).length;   // the inlet's (drawn first), then the harbour's
const SAIL_STREAK_COL = SAIL_PALS.map(p => p[4]);

/* ================= Pre-render everything static ================= */
let HL, SKY_C, CLOUDS, MTN, WISPS, NV, SP, CPL, SAIL_C, DT, BB_C, POSTERS, PITCH, WATER_C, SEABUS_C, TUG_C, BARGE_C,
  FREIGHT_C, FP_C, FPD_C, BOAT_C, HERON_C, HERON_F, HERON_B, UMB_C, PERSON_C, GULL_SPR, PERCH_C, PERCH_F, FLOAT_C, PILE_C, VIG_C,
  SAIL_HL, SAIL_RAIN, DUCK_C;
function prerender() {
  HL = {
    city: makeHalo(110, '#ffa870', 0.08, 1.6), nv: makeHalo(60, '#ffb070', 0.1, 1.6),
    run: makeHalo(20, '#c8d8ff', 0.12, 1.5), chalet: makeHalo(10, '#ffd08a', 0.35, 1.4), tram: makeHalo(4, '#ffb080', 0.5, 1),
    red: makeHalo(4, '#ff3a30', 0.6, 1), white: makeHalo(4, '#ffffff', 0.6, 1), green: makeHalo(4, '#40ff80', 0.5, 1),
    win: makeHalo(6, '#ffc070', 0.2), quay: makeHalo(18, '#ffc680', 0.24, 1.5), lh: makeHalo(18, '#fff6e0', 0.55, 1.4),
    lhCore: makeHalo(5, '#ffffff', 0.8, 1), spLamp: makeHalo(6, '#ffd898', 0.4, 1.3), cpLamp: makeHalo(4, '#ffe6b0', 0.45, 1.2),
    hotel: makeHalo(24, '#ffd090', 0.12, 1.6), hcRing: makeHalo(16, '#ffd89a', 0.26, 1.5), lift: makeHalo(4, '#ffe8b0', 0.6, 1),
    ccw: makeHalo(40, '#ffc880', 0.14, 1.6), ship: makeHalo(12, '#ffd8a0', 0.3), boat: makeHalo(8, '#ffc060', 0.34),
    bbGlow: makeHalo(28, '#fff0d0', 0.16, 1.6), bbLamp: makeHalo(6, '#fff0c8', 0.55, 1.3),
    lamp: makeHalo(56, '#ffb866', 0.26, 1.7), lampCore: makeHalo(14, '#ffe6b0', 0.5, 1.4), mast: makeHalo(5, '#ffffff', 0.5, 1.2),
    head: makeHalo(8, '#f4f8ff', 0.5, 1.2), rear: makeHalo(3, '#ff3030', 0.6, 1), cabin: makeHalo(7, '#ffc070', 0.3),
  };
  SAIL_HL = SAIL_PALS.map((p, i) => makeHalo(22, p[3], i ? 0.2 : 0.13, 1.5));
  SAIL_RAIN = SAIL_PALS.map(p => [p[4], p[4], p[5]].map((c, d) => rgba(c, [0.4, 0.6, 0.75][d])));
  SKY_C = renderSky(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  MTN = renderMountains(); WISPS = [renderWisp(91, 26, 0.8), renderWisp(92, 22, 0.65)];
  NV = renderNorthVan(); SP = renderStanley(); CPL = renderCanadaPlace();
  SAIL_C = SAILS.map((s, k) => SAIL_PALS.map(pal => renderSail(k, pal)));
  DT = renderDowntown();
  BB_C = renderBillboard(); POSTERS = [renderPoster(false, false), renderPoster(true, false), renderPoster(false, true), renderPoster(true, true)];
  PITCH = makePitch(BB.w, BB.h, { accent: '#5ce1e6' });
  WATER_C = renderWater();
  SEABUS_C = sprite(SEABUS_ROWS, SEABUS_KEY); TUG_C = sprite(TUG_ROWS, TUG_KEY); BARGE_C = renderBarge(); FREIGHT_C = renderFreighter();
  FP_C = FP_KEY.map(key => sprite(FP_ROWS, key)); FPD_C = renderFloatDock();
  BOAT_C = BOATS.map(renderBoat);
  HERON_C = sprite(HERON_ROWS, HERON_KEY); HERON_F = sprite(HERON_ROWS, HERON_KEY, true); HERON_B = sprite(HERON_BODY, HERON_KEY);
  UMB_C = sprite(UMB_ROWS, UMB_KEY); PERSON_C = sprite(PERSON_ROWS, PERSON_KEY);
  GULL_SPR = makeGulls(); PERCH_C = sprite(PERCH_ROWS, PERCH_KEY); PERCH_F = sprite(PERCH_ROWS, PERCH_KEY, true);
  DUCK_C = [[sprite(DUCK_ROWS, DUCK_KEY), sprite(DUCK_ROWS, DUCK_KEY, true)], [sprite(DUCK_HEN, HEN_KEY), sprite(DUCK_HEN, HEN_KEY, true)]];
  FLOAT_C = renderFloat(); PILE_C = renderPile(); VIG_C = renderVignette();
}

/* ================= Per-frame: sky, mountains, wisps ================= */
function flick(t, list, id) {
  let v = 1;
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 20)); v = hv > 0.45 ? 1 : hv > 0.2 ? 0.4 : 0.1; }
  return v;
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  CLOUDS.forEach((L, i) => {
    const off = scroll(t, L.k, W);
    ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y);
    if (i === 0) drawPlane(t);
  });
  halo(HL.city, 440, 150, 0.9);
}
/* a late arrival letting down toward the airport, blinking between the cloud decks */
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(488 - u * 500), y = Math.round(30 + u * 16);
  rect(ctx, x, y, 4, 1, '#2a3040'); px(ctx, x + 2, y - 1, '#2a3040');
  halo(HL.white, x - 1, y, 0.6); px(ctx, x - 1, y, '#fff8e8');
  if (frac(t / 1.2) < 0.1) { px(ctx, x + 2, y, '#ffffff'); halo(HL.white, x + 2, y, 1); }
  if (frac(t / 1.5 + 0.3) < 0.5) { px(ctx, x + 3, y - 1, '#ff3a3a'); halo(HL.red, x + 3, y - 1, 0.6); }
}
function drawMountains(t) {
  ctx.drawImage(MTN.c, 0, 0);
  const s = step(t, 480);
  for (const l of MTN.runLights) px(ctx, l.x, l.y, hash(l.id, s, 5) < 0.9 ? '#fff4dc' : '#9a8e78');
  halo(HL.run, 121, 90, 0.75 + 0.1 * wave(t, 6));
  const st = step(t, 240);
  for (const l of MTN.twinkle) if (hash(l.id, st, 6) < 0.72) px(ctx, l.x, l.y, l.col);
  halo(HL.chalet, CHALET.x, CHALET.y, 0.9);
  // the tram: two cabins on one cable, passing mid-way (one up, one down, a round trip a minute)
  const u = 0.5 - 0.5 * Math.cos(TAU * 4 * t / LOOP), top = [CHALET.x - 3, CHALET.y + 2];
  for (const v of [u, 1 - u]) {
    const x = Math.round(lerp(TRAM_BASE.x, top[0], v)), y = Math.round(lerp(TRAM_BASE.y, top[1], v));
    rect(ctx, x - 1, y + 1, 2, 2, '#c8403a'); px(ctx, x, y + 1, '#ffd8a0');
    halo(HL.tram, x, y + 1, 0.4);
  }
  // the wind turbine on the summit, its three blades turning, a red light on the nacelle
  const hx = GROUSE.x, hy = GROUSE.y - 9, a0 = TAU / 3 * 90 * t / LOOP;
  ctx.fillStyle = '#8a92a4';
  for (let b = 0; b < 3; b++) {
    const a = a0 + b * TAU / 3, sa = Math.sin(a), ca = Math.cos(a);
    for (let k = 1; k <= 4; k++) ctx.fillRect(Math.round(hx + sa * k), Math.round(hy - ca * k), 1, 1);
  }
  const on = frac(t / 2) < 0.5;
  px(ctx, hx, hy, on ? '#ff4a3a' : '#5a2020'); if (on) halo(HL.red, hx, hy, 0.8);
}
function drawWisps(t) {
  const W0 = WISPS[0], W1 = WISPS[1];
  ctx.globalAlpha = 0.6 + 0.2 * wave(t, 3, 0.1);
  let off = scroll(t, 2, W); ctx.drawImage(W0, off - W, 80); ctx.drawImage(W0, off, 80);
  ctx.globalAlpha = 0.5 + 0.2 * wave(t, 5, 0.6);
  off = scroll(t, 3, W); ctx.drawImage(W1, off - W, 104); ctx.drawImage(W1, off, 104);
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: North Vancouver and the inlet ================= */
function drawNorthVan(t) {
  ctx.drawImage(NV.c, 0, 0);
  for (const w of NV.wins) if (winOn(w, 20, t)) px(ctx, w.x, w.y, w.col);
  halo(HL.nv, 226, 146, 0.8);
  halo(HL.quay, 226, 152, 0.9 + 0.1 * wave(t, 9));
}
function drawInlet(t) {
  ctx.drawImage(WATER_C, 0, 0, 292, DOCK - WL_FAR, 0, WL_FAR, 292, DOCK - WL_FAR);
  reflect(ctx, t, { top: WL_FAR, rows: DOCK - WL_FAR, x: 0, w: 292, squash: 1.6, alpha: 0.46, amp: 0.7, grow: 0.05 });
  for (let k = 0; k < NV_STREAKS; k++) { const s = STREAKS[k]; streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.y0 + 1, DOCK); }
}
/** a vessel sprite's rippled reflection under its waterline */
function mirror(spr, x, wl, rows, a = 0.32) {
  const w = spr.width, h = spr.height;
  for (let j = 0; j < rows; j++) {
    const off = Math.round(Math.sin(TAU * 45 * t_ / LOOP + j * 0.7) * (0.4 + j * 0.12));
    ctx.globalAlpha = a * (1 - j / rows);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, wl + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
}
function drawFreighter(t) {
  const { x, wl } = FREIGHTER, spr = FREIGHT_C, y = wl - spr.height + 1;
  mirror(spr, x, wl, 12, 0.28);
  ctx.drawImage(spr, x, y);
  halo(HL.ship, x + 44, y + 6, 0.8);
  for (const dx of [10, 20, 30]) halo(HL.boat, x + dx, y + 10, 0.5);
  px(ctx, x + 2, y + 9, '#ffffff'); halo(HL.white, x + 2, y + 9, 0.8);       // anchor lights, fore and aft
  px(ctx, x + 50, y + 11, '#ffffff'); halo(HL.white, x + 50, y + 11, 0.6);
  streak(ctx, t, x + 44, 5, '#ffd48a', 0.22, 7, wl + 1, wl + 26);
}
/** a pass: the wake, the bow wave, the sprite and its lights */
function wake(x, w, wl, dir, t) {
  const s = step(t, 1200), stern = dir > 0 ? x + 1 : x + w - 2, bow = dir > 0 ? x + w - 1 : x;
  ctx.fillStyle = '#c8d4e2';
  for (let d = 1; d < 30; d++) {
    const xx = stern - dir * d, fade = 1 - d / 30;
    if (hash(d, s, 41) < 0.6) { ctx.globalAlpha = 0.55 * fade; ctx.fillRect(xx, wl + 1 + Math.round(d * 0.05), 1, 1); }
  }
  ctx.globalAlpha = 0.8;
  for (let k = 0; k < 4; k++) if (hash(k, s, 45) < 0.8) ctx.fillRect(bow + dir * (k >> 1), wl - (k & 1), 1, 1);
  ctx.globalAlpha = 1;
}
function seaBusAt(t) {
  let u, from, to;
  if (t >= SB.out[0] && t < SB.out[1]) { u = (t - SB.out[0]) / (SB.out[1] - SB.out[0]); from = 0; to = 1; }
  else if (t >= SB.back[0] && t < SB.back[1]) { u = (t - SB.back[0]) / (SB.back[1] - SB.back[0]); from = 1; to = 0; }
  else if (t >= SB.out[1] && t < SB.back[0]) return { x: SB.bx, wl: SB.bwl, dir: 0 };
  else return null;
  const e = smooth(0, 1, u) * 0.7 + u * 0.3, v = lerp(from, to, e);
  return { x: Math.round(lerp(SB.ax, SB.bx, v)), wl: Math.round(lerp(SB.awl, SB.bwl, v)), dir: to > from ? -1 : 1 };
}
function drawSeaBus(t) {
  const s = seaBusAt(t);
  if (!s) return;
  const { x, wl, dir } = s, spr = SEABUS_C, y = wl - spr.height + 1;
  mirror(spr, x, wl, Math.min(10, WL - wl), 0.34);
  if (dir) wake(x, spr.width, wl, dir, t);
  ctx.drawImage(spr, x, y);
  for (const o of [5, 12, 18]) streak(ctx, t, x + o, 2, '#ffe0a0', 0.3, o, wl + 1, Math.min(DOCK, wl + 30));
  halo(HL.cabin, x + 12, y + 3, 0.8);
  px(ctx, x + 12, y + 1, '#ffffff'); halo(HL.white, x + 12, y + 1, 0.7);       // masthead light
  if (dir < 0) { px(ctx, x + 1, y + 4, '#ff4040'); halo(HL.red, x + 1, y + 4, 0.7); }        // out: its port side toward us
  else if (dir > 0) { px(ctx, x + 22, y + 4, '#40ff80'); halo(HL.green, x + 22, y + 4, 0.7); } // back: starboard
}
function drawTug(t) {
  if (t < TUG.t0 || t >= TUG.t1) return;
  const u = (t - TUG.t0) / (TUG.t1 - TUG.t0), x = Math.round(lerp(TUG.x0, TUG.x1, u)), wl = TUG.wl;
  const bx = x + 30, bwl = wl + 1;                                     // the barge trails on its towline
  mirror(BARGE_C, bx, bwl, 6, 0.28);
  ctx.drawImage(BARGE_C, bx, bwl - BARGE_C.height + 1);
  const sag = 1 + Math.round(0.6 * (1 + wave(t, 40)));
  pline(x + 13, wl - 3, x + 22, wl - 2 + sag, '#2a2a30'); pline(x + 22, wl - 2 + sag, bx, bwl - 3, '#2a2a30');
  mirror(TUG_C, x, wl, 8, 0.32);
  wake(x, TUG_C.width, wl, -1, t);
  ctx.drawImage(TUG_C, x, wl - TUG_C.height + 1);
  const y = wl - TUG_C.height + 1;
  halo(HL.cabin, x + 5, y + 4, 0.8); streak(ctx, t, x + 5, 3, '#ffd890', 0.3, 9, wl + 1, wl + 22);
  for (const dy of [0, 2]) { px(ctx, x + 4, y + dy, '#ffffff'); halo(HL.white, x + 4, y + dy, 0.6); }   // towing lights
  px(ctx, x + 13, y + 5, '#ffcf40'); halo(HL.boat, x + 13, y + 5, 0.6);
  px(ctx, bx + 33, bwl - 3, '#ffffff'); halo(HL.white, bx + 33, bwl - 3, 0.5);
}
function gullFrame(t, g) { return wave(t, g.kf, g.pf) > 0.3 ? [0, 1, 2, 1][step(t, 1200) % 4] : 1; }
function drawFarGulls(t) {
  for (const g of FAR_GULLS) {
    const a = TAU * (g.k * t / LOOP + g.ph), x = g.cx + g.rx * Math.cos(a), y = g.cy + g.ry * Math.sin(a);
    ctx.drawImage(GULL_SPR[0][gullFrame(t, g)], Math.round(x) - 2, Math.round(y) - 1);
  }
}

/* ================= Per-frame: Stanley Park, downtown, the billboard, Canada Place ================= */
function lighthouse(t) { const ph = frac(t / 3); return ph < 0.14 ? 1 - ph / 0.14 : 0; }   // a white flash every 3 s
function drawStanley(t) {
  ctx.drawImage(SP.c, 0, 0);
  const lf = flick(t, SP_FLICK, 3);
  SP_LAMPS.forEach((lx, i) => halo(HL.spLamp, lx, 182, (i === 3 ? lf : 1) * 0.9));
  if (lf < 0.5) px(ctx, SP_LAMPS[3], 182, '#5a4a30');
  const fl = lighthouse(t);
  rect(ctx, LH.x - 1, LH.y - 1, 3, 3, fl > 0.25 ? '#fffbe8' : '#b89a60');
  halo(HL.lhCore, LH.x, LH.y, 0.25 + 0.75 * fl); if (fl > 0) halo(HL.lh, LH.x, LH.y, fl);
  if (t >= JOGGER.t0 && t < JOGGER.t1) {                              // a runner with a headlamp on the seawall
    const u = (t - JOGGER.t0) / (JOGGER.t1 - JOGGER.t0), x = Math.round(lerp(JOGGER.x0, JOGGER.x1, u)), y = JOGGER.y;
    const stride = step(t, 1440) & 1;
    rect(ctx, x, y - 4, 1, 3, '#1a1c24'); px(ctx, x - stride, y - 1, '#1a1c24'); px(ctx, x + 1 - stride, y - 1, '#1a1c24');
    px(ctx, x + 1, y - 4, '#ffffff'); halo(HL.head, x + 2, y - 3, 0.7);
    ctx.globalAlpha = 0.35; rect(ctx, x + 3, y - 2, 4, 1, '#dfe8ff'); ctx.globalAlpha = 1;
  }
}
function drawBillboard(t, pitch) {
  const lv = flick(t, BB_FLICK, 17);
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + 6, (0.85 + 0.1 * wave(t, 8, 0.6)) * (2 + lv) / 3);
  ctx.drawImage(BB_C, BBC.x, BBC.y);
  if (!PITCH.covers(t, pitch)) {
    const burst = hash(step(t, 60), 0, 61) < 0.5, tail = burst && (step(t, 960) & 1) ? 1 : 0;   // the salmon's tail flicks
    ctx.drawImage(POSTERS[tail], BB.x, BB.y);
    if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(POSTERS[2 + tail], BB.x, BB.y); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => {
    const a = (i === 2 ? lv : 1) * (0.92 + 0.08 * wave(t, 640 + i * 41));
    if (a < 0.9) rect(ctx, lx - 2, BB_LAMP_Y, 5, 1, `rgba(40,30,20,${(0.85 * (1 - a)).toFixed(3)})`);
    halo(HL.bbLamp, lx, BB_LAMP_Y + 1, a);
  });
}
function drawDowntown(t, pitch, billboard) {
  ctx.drawImage(DT.c, 0, 0);
  for (const w of DT.wins) if (winOn(w, 50, t)) px(ctx, w.x, w.y, w.col);
  halo(HL.ccw, 420, 184, 0.9);
  const cx = HC.cx, rp = frac(2 * t / LOOP);                          // the revolving restaurant's brighter windows creep round
  for (let k = 0; k < 3; k++) px(ctx, cx - 9 + Math.floor(frac(rp + k * 0.05) * 19), HC.disc + 2, '#fff6e0');
  halo(HL.hcRing, cx, HC.disc + 2, 0.75);
  const ly = Math.round(lerp(CCW.roofR - 3, HC.disc + 6, 0.5 - 0.5 * Math.cos(TAU * 6 * t / LOOP)));   // the glass lift
  rect(ctx, cx - 2, ly, 1, 2, '#ffe8b0'); halo(HL.lift, cx - 2, ly, 0.7);
  const on = frac(t / 2 + 0.25) < 0.5;
  px(ctx, cx, HC.top, on ? '#ff4a3a' : '#5a2020'); if (on) halo(HL.red, cx, HC.top, 0.9);
  if (billboard) drawBillboard(t, pitch);
  if (t >= CYCLIST.t0 && t < CYCLIST.t1) {                            // a cyclist on the seawall, headlamp and a red tail light
    const u = (t - CYCLIST.t0) / (CYCLIST.t1 - CYCLIST.t0), x = Math.round(lerp(CYCLIST.x0, CYCLIST.x1, u)), y = CYCLIST.y;
    rect(ctx, x - 1, y, 4, 1, '#1a1c22'); rect(ctx, x, y - 3, 2, 3, '#20242c'); px(ctx, x + 1, y - 4, '#20242c');
    px(ctx, x - 1, y - 1, '#ffffff'); halo(HL.head, x - 2, y - 1, 0.8);
    ctx.globalAlpha = 0.3; rect(ctx, x - 7, y, 5, 1, '#e8f0ff'); ctx.globalAlpha = 1;
    px(ctx, x + 3, y - 1, '#ff3a30'); halo(HL.rear, x + 3, y - 1, 0.8);
  }
}
const sailIdx = [0, 0, 0, 0, 0];                     // the dominant light of each sail this frame (the rain and water take it)
const SAIL_A = [0, 0, 0, 0, 0], SAIL_B = [0, 0, 0, 0, 0], SAIL_F = [0, 0, 0, 0, 0];   // each sail's light, the next, the fade
function sailState(t) {
  for (let k = 0; k < SAILS.length; k++) {
    SAIL_A[k] = SAIL_B[k] = SAIL_F[k] = 0;
    for (const s of SAIL_SHOWS) {
      const tau = t - s.t0 - k * s.lag, i = Math.floor(tau / s.dwell);
      if (tau < 0 || i >= s.seq.length - 1) continue;
      SAIL_A[k] = s.seq[i]; SAIL_B[k] = s.seq[i + 1]; SAIL_F[k] = smooth(0, s.fade, tau - i * s.dwell);
    }
    sailIdx[k] = SAIL_F[k] > 0.5 ? SAIL_B[k] : SAIL_A[k];
  }
}
function drawCanadaPlace(t) {
  halo(HL.hotel, 340, 132, 0.7);
  ctx.drawImage(CPL.c, 0, 0);
  for (const w of CPL.wins) if (winOn(w, 70, t)) px(ctx, w.x, w.y, WARM_A);
  for (let k = 0; k < SAILS.length; k++) {
    const a = SAIL_A[k], b = SAIL_B[k], f = SAIL_F[k], A = SAIL_C[k][a], B = SAIL_C[k][b], br = 0.8 + 0.12 * wave(t, 12, k * 0.2);
    ctx.drawImage(A.c, A.x, A.y);
    if (f > 0) { ctx.globalAlpha = f; ctx.drawImage(B.c, B.x, B.y); ctx.globalAlpha = 1; }
    halo(SAIL_HL[a], SAILS[k][0], 154, (1 - f) * br); if (f > 0) halo(SAIL_HL[b], SAILS[k][0], 154, f * br);
  }
  for (const lx of CP_LAMPS) halo(HL.cpLamp, lx, CP.deck - 3, 0.8);
  CP_FLAGS.forEach((fx, i) => {                                       // flags on the prow, stirring in the breeze
    const fl = wave(t, 150 + i * 13, i * 0.3) > 0.2 ? 1 : 0, y = CP.deck - 8;
    px(ctx, fx + 1, y, '#d8323a'); px(ctx, fx + 2, y + fl, '#e8e4e8'); px(ctx, fx + 1, y + 1, '#b02830'); px(ctx, fx + 2, y + 1 - fl, '#d8323a');
  });
}

/* ================= Per-frame: Coal Harbour in front of us ================= */
function drawRings(list, t, x0, x1, y0, y1, maxR, waterOnly) {
  ctx.fillStyle = '#9ab0c8';
  for (const g of list) {
    const { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.45) continue;
    const s = u / 0.45, x = Math.floor(x0 + hash(g.id, cyc, 1) * (x1 - x0)), y = Math.floor(y0 + hash(g.id, cyc, 2) * (y1 - y0));
    if (waterOnly && y < waterlineAt(x) + 3) continue;
    const rr = 1 + s * maxR, ry = rr * 0.36, pts = 6 + Math.floor(rr * 2.5);
    ctx.globalAlpha = (1 - s) * 0.45;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}
function drawHarbour(t, rain) {
  const rows = DOCK - WL;
  ctx.drawImage(WATER_C, 0, WL - WL_FAR, 150, rows, 0, WL, 150, rows);
  ctx.drawImage(WATER_C, CP.x0, WL - WL_FAR, W - CP.x0, rows, CP.x0, WL, W - CP.x0, rows);
  reflect(ctx, t, { top: WL, rows, x: 0, w: 150, squash: 1.5, alpha: 0.48, amp: 0.8, grow: 0.07 });
  reflect(ctx, t, { top: WL, rows, x: CP.x0, w: W - CP.x0, squash: 1.5, alpha: 0.52, amp: 0.8, grow: 0.07 });
  for (let y = WL + 2, n = 0; y < DOCK; y += 2, n++) for (let j = 0; j < 3; j++) {   // slow swell lines across the harbour
    const k = 2 + ((n * 7 + j * 5) % 5), len = 5 + Math.floor(hash(n, j, 41) * 16);
    const x = Math.round(frac(hash(n, j, 40) + k * t / LOOP) * (W + 40)) - 20;
    if (y < waterlineAt(x) + 2 || y < waterlineAt(x + len) + 2) continue;
    ctx.globalAlpha = j === 2 ? 0.22 : 0.34;
    rect(ctx, x, y + (j & 1), len, 1, j === 2 ? '#7a8ca4' : '#060a12');
  }
  ctx.globalAlpha = 1;
  for (let k = NV_STREAKS; k < STREAKS.length; k++) { const s = STREAKS[k]; streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.y0 + 1, DOCK); }
  SAILS.forEach(([x], k) => streak(ctx, t, x, 5, SAIL_STREAK_COL[sailIdx[k]], 0.3, 50 + k, WL + 1, DOCK));
  const fl = lighthouse(t);
  if (fl > 0.05) streak(ctx, t, LH.x, 3, '#fff6e0', 0.4 * fl, 60, WL + 1, DOCK);
  if (rain) drawRings(RINGS, t, 0, W, WL_FAR + 3, DOCK - 2, 5, true);
}
function drawFloatplanes(t) {
  ctx.drawImage(FPD_C, 386, FP_WL - 4);
  PLANES.forEach((pl, i) => {
    const spr = FP_C[i], dy = Math.round(0.6 * wave(t, pl.k, pl.ph)), wl = FP_WL + 5 + dy;
    mirror(spr, pl.x, wl, 8, 0.3);
    ctx.drawImage(spr, pl.x, wl - spr.height + 1);
  });
  px(ctx, 478, FP_WL - 4, '#ffe0a8'); halo(HL.boat, 478, FP_WL - 4, 0.8);
}
function drawMidGulls(t) {
  for (const g of MID_GULLS) {
    const a = TAU * (g.k * t / LOOP + g.ph), x = g.cx + g.rx * Math.sin(a), y = g.cy + g.ry * Math.sin(2 * a);
    ctx.drawImage(GULL_SPR[1][gullFrame(t, g)], Math.round(x) - 4, Math.round(y) - 2);
  }
}
function drawHeron(t) {
  mirror(PILE_C, PILE.x - 2, PILE.wl, DOCK - PILE.wl - 1, 0.3);
  ctx.drawImage(PILE_C, PILE.x - 2, PILE.top);
  const x = PILE.x - 6, y = PILE.top - 16, ds = t - STRIKE;
  if (ds >= 0 && ds < 1.3) {                                         // the strike: neck shot down to the water and back
    ctx.drawImage(HERON_B, x, y);
    const e = ds < 0.35 ? smooth(0, 0.35, ds) : ds < 0.8 ? 1 : 1 - smooth(0.8, 1.3, ds);
    const sx = x + 4, sy = y + 7, tx = Math.round(lerp(x + 3, PILE.x - 13, e)), ty = Math.round(lerp(y + 1, PILE.wl + 1, e));
    pline(sx, sy, tx + 1, ty, '#d6dae2'); pline(sx + 1, sy + 1, tx + 2, ty, '#6a7a90');
    rect(ctx, tx, ty - 1, 2, 2, '#d6dae2'); px(ctx, tx - 1, ty, '#d8a040'); px(ctx, tx - 2, ty + 1, '#d8a040');
    if (ds > 0.3 && ds < 1.2) {                                       // the splash
      const v = (ds - 0.3) / 0.9;
      ctx.fillStyle = '#dfe8f2';
      for (let k = 0; k < 6; k++) {
        const a = -Math.PI / 2 + (k - 2.5) * 0.45, d = v * 9;
        ctx.globalAlpha = 1 - v;
        ctx.fillRect(Math.round(PILE.x - 13 + Math.cos(a) * d), Math.round(PILE.wl - 1 + Math.sin(a) * d * 0.6 + v * v * 7), 1, 1);
      }
      ctx.globalAlpha = 1;
    }
  } else {
    const look = hash(7, step(t, 60), 3) < 0.3 && !(ds >= 1.3 && ds < 6);
    ctx.drawImage(look ? HERON_F : HERON_C, x, y);
    if (ds >= 1.3 && ds < 5.5) {                                      // a small fish, wriggling in its bill
      const wr = step(t, 1440) & 1;
      px(ctx, x - 1, y + 2, '#c8d4e0'); px(ctx, x - 2, y + 2 + wr, '#9aa8b8'); px(ctx, x - 3, y + 3 - wr, '#9aa8b8');
    }
  }
}
function drawDucks(t) {
  for (const d of DUCKS) {
    const a = TAU * (t / LOOP + d.ph), x = Math.round(d.cx + d.rx * Math.cos(a)), y = Math.round(d.cy + d.ry * Math.sin(a)), left = Math.sin(a) > 0;
    const [a0, a1] = d.dive, under = t >= a0 + 0.4 && t < a1 - 0.3;
    ctx.fillStyle = '#8a9cb4';                                          // the V of its wake, or the rings where it dived
    if (under || (t >= a0 && t < a0 + 1.6) || (t >= a1 - 0.3 && t < a1 + 1)) {
      const s = t < a0 + 1.6 ? (t - a0) / 1.6 : (t - a1 + 0.3) / 1.3, rr = 2 + s * 5;
      ctx.globalAlpha = 0.5 * (1 - Math.min(1, s));
      for (let j = 0; j < 14; j++) { const b = j / 14 * TAU; ctx.fillRect(Math.round(x + 3 + Math.cos(b) * rr), Math.round(y + 3 + Math.sin(b) * rr * 0.35), 1, 1); }
    }
    if (!under) {
      ctx.globalAlpha = 0.35;
      for (let k = 1; k < 9; k++) { const bx = left ? x + 5 + k : x - k; ctx.fillRect(bx, y + 3 + (k >> 2), 1, 1); ctx.fillRect(bx, y + 3 - (k >> 2), 1, 1); }
      ctx.globalAlpha = 1;
      const bob = t >= a1 - 0.3 && t < a1 ? 1 : 0;
      ctx.drawImage(DUCK_C[d.hen ? 1 : 0][left ? 0 : 1], x, y - 1 + bob);
    }
    ctx.globalAlpha = 1;
  }
}
function drawBoats(t, G) {
  BOATS.forEach((b, i) => {
    const B = BOAT_C[i], dy = Math.round(0.7 * wave(t, b.k, b.ph)), wl = b.wl + dy, y = wl - B.h + 1;
    mirror(B.c, b.x0, wl, Math.min(9, DOCK - wl - 1), 0.3);
    ctx.drawImage(B.c, b.x0, y);
    const sway = (0.8 + 1.2 * G) * wave(t, b.k - 4, b.ph + 0.3), mx = b.mast, foot = wl - 14, topX = mx + sway, topY = b.top + dy;
    ctx.globalAlpha = 0.2; ctx.fillStyle = '#9aa0ac';                   // the mast's wavering reflection
    for (let yy = wl + 2; yy < DOCK; yy += 2) ctx.fillRect(Math.round(mx - sway * 0.3 + Math.sin(TAU * 45 * t / LOOP + yy * 0.6)), yy, 1, 1);
    ctx.globalAlpha = 0.5;
    const bowX = b.dir > 0 ? b.x1 - 1 : b.x0 + 1, sternX = b.dir > 0 ? b.x0 + 2 : b.x1 - 2;
    pline(topX, topY + 1, bowX, wl - 8, '#4a505c'); pline(topX, topY + 1, sternX, wl - 7, '#4a505c');
    pline(topX, topY + 12, mx - 6, wl - 8, '#3a404a'); pline(topX, topY + 12, mx + 6, wl - 8, '#3a404a');
    ctx.globalAlpha = 1;
    pline(mx, foot, topX, topY, '#8a909c'); pline(mx + 1, foot, topX + 1, topY + 2, '#4a505a');
    px(ctx, Math.round(topX), topY - 1, '#ffffff'); halo(HL.mast, topX, topY - 1, 0.85);   // the anchor light at the masthead
    halo(HL.cabin, (b.x0 + b.x1) >> 1, wl - 12, 0.5);
  });
}

/* ================= Per-frame: our float, the lamp, the umbrella ================= */
function drawLamp(t) {
  const lf = flick(t, LAMP_FLICK, 11), li = (0.94 + 0.06 * wave(t, 1500, 0.3)) * lf;
  rect(ctx, LAMP.x - 2, LAMP.top + 1, 4, 6, lf > 0.5 ? '#ffe4a8' : '#6a5030');
  rect(ctx, LAMP.x - 1, LAMP.top + 2, 2, 3, lf > 0.5 ? '#fff8e0' : '#8a6a40');
  halo(HL.lamp, LAMP.x, LAMP.top + 4, li); halo(HL.lampCore, LAMP.x, LAMP.top + 4, li);
}
function drawUmbrella(t, G, rain) {
  const sw = Math.round(G * 1.5 * wave(t, 13, 0.4)), ux = UMB.x - 7 + sw, uy = UMB.y - 22;
  reflect(ctx, t, { top: UMB.y + 1, rows: H - UMB.y - 1, x: UMB.x - 9, w: 18, squash: 1.2, alpha: 0.26, k1: 40, k2: 90 });
  ctx.drawImage(PERSON_C, UMB.x - 3, UMB.y - 15);
  ctx.fillStyle = '#4a4a50'; ctx.fillRect(UMB.x - 3, UMB.y - 12, 1, 7); px(ctx, UMB.x - 2, UMB.y - 14, '#44464e');   // lit from the lamp
  pline(UMB.x + Math.round(sw / 2), uy + 4, UMB.x, UMB.y - 9, '#1a1a1e');
  ctx.drawImage(UMB_C, ux, uy);
  ctx.fillStyle = '#e8645a';                                         // the lamp's light on the left of the canopy
  ctx.fillRect(ux + 2, uy + 1, 3, 1); ctx.fillRect(ux + 1, uy + 2, 3, 1); px(ctx, ux + 5, uy, '#e8645a');
  if (!rain) return;
  ctx.fillStyle = '#b8c8dc';                                          // drips off the rim
  for (let k = 0; k < 4; k++) {
    const { u } = cycle(t, 150 + k * 17, k * 0.23), dx = [0, 14, 4, 10][k], dy0 = k < 2 ? 5 : 4;
    ctx.globalAlpha = 0.7 * (1 - u);
    ctx.fillRect(ux + dx, Math.round(uy + dy0 + u * (UMB.y - uy - dy0 - 1)), 1, 2);
  }
  ctx.globalAlpha = 1;
}
function drawForeground(t, G, rain) {
  ctx.drawImage(FLOAT_C, 0, 0);
  if (rain) drawRings(PUDDLE_RINGS, t, 20, 460, DOCK + 5, H - 3, 3, false);
  streak(ctx, t, LAMP.x, 5, '#ffc878', 0.34, 21, DOCK + 3, H);
  ctx.drawImage(hash(4, step(t, 120), 2) < 0.5 ? PERCH_F : PERCH_C, 413, DOCK - 9);   // a gull on the dock box
  drawUmbrella(t, G, rain);
  drawLamp(t);
}
function drawNearGulls(t) {
  for (const g of NEAR_GULLS) {
    if (t < g.a || t >= g.b) continue;
    const s = (t - g.a) / (g.b - g.a), x = lerp(-20, W + 6, s * s * 0.3 + s * 0.7), y = g.y0 + g.dip * Math.sin(Math.PI * s);
    const fr = (s < 0.25 || (s > 0.55 && s < 0.75)) ? [0, 1, 2, 1][step(t, 1680) % 4] : 1;
    ctx.drawImage(GULL_SPR[2][fr], Math.round(x), Math.round(y));
  }
}

/* ================= Per-frame: rain ================= */
function drawRain(L, t, G, lights) {
  const sl = L.s0 + L.s1 * G, len = L.len, span = W + 30 + L.fall * (L.s0 + L.s1), SR = SAIL_RAIN, d0 = L.depth;
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const xl = -30 + hash(d.id, cyc, 11) * span;
    const yl = L.land(Math.round(xl), hash(d.id, cyc, 12), hash(d.id, cyc, 13));
    if (u < L.ff) {
      const f = u / L.ff, yh = yl - L.fall * (1 - f), xh = xl - (yl - yh) * sl;
      if (yh < -len || xh < -len || xh > W + len) continue;
      const lit = litAt(xh, yh, L.far, lights);
      ctx.fillStyle = lit === 1 ? L.warm : lit === 2 ? L.cool : lit === 3 ? SR[sailIdx[clamp(Math.round((xh - SAILS[0][0]) / SAIL_GAP), 0, 4)]][d0] : L.col;
      for (let k = 0; k < len; k++) ctx.fillRect(Math.round(xh - k * sl), Math.round(yh - k), 1, 1);
    } else {
      const s = (u - L.ff) / (1 - L.ff), big = L.len > 6;
      const hgt = Math.sin(Math.PI * s) * (big ? 2.5 : 1.2), spread = 1 + s * (big ? 2.5 : 1.2);
      ctx.globalAlpha = 1 - s;
      ctx.fillStyle = litAt(xl, yl, false, lights) === 1 ? L.warm : L.splash;
      const Y = Math.round(yl - 1 - hgt);
      ctx.fillRect(Math.round(xl - spread + 1), Y, 1, 1); ctx.fillRect(Math.round(xl + spread + 1), Y, 1, 1);
      if (s < 0.35) ctx.fillRect(Math.round(xl), Math.round(yl - 1), 1, 1);
      ctx.globalAlpha = 1;
    }
  }
}

/* ================= Frame compositor (back to front) ================= */
let t_ = 0;                                          // the frame's time, for the reflection helper
// pitch: the "your ad here" face (promo captures) on the billboard; billboard: false leaves it out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  t_ = t;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const G = breeze(t), lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS, wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  sailState(t);
  drawSky(t);
  drawMountains(t);
  drawWisps(t);
  drawFarGulls(t);
  drawNorthVan(t);
  drawInlet(t);
  drawFreighter(t);
  drawTug(t);
  drawSeaBus(t);
  if (wx.rain) drawRain(RAIN[0], t, G, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawStanley(t);
  drawDowntown(t, pitch, billboard);
  drawCanadaPlace(t);
  drawHarbour(t, wx.rain);
  drawFloatplanes(t);
  drawMidGulls(t);
  drawDucks(t);
  drawHeron(t);
  drawBoats(t, G);
  if (wx.rain) drawRain(RAIN[1], t, G, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawForeground(t, G, wx.rain);
  if (wx.rain) drawRain(RAIN[2], t, G, lights);
  drawNearGulls(t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const pan = x => +(x / W * 2 - 1).toFixed(2);
export default {
  id: 'vancouver',
  name: 'Vancouver',
  tagline: 'Rain over Coal Harbour',
  timeZone: 'America/Vancouver',
  accent: '#5ce1e6',
  focusX: SAILS[2][0],
  landmark: [SAIL_L, CP.x1],                           // Canada Place: its sails, the pier under them and the hotel tower
  landmarkTop: HOTEL.top,                              // the row of the hotel tower's roof, the landmark's highest point
  billboard: BB,
  ambience: { rain: 0.55, water: 0.4, wind: 0.15, traffic: 0.08 },
  events: [
    { t: HORNS[0], type: 'horn', x: 272, gain: 0.5 },
    { t: SB.out[0], type: 'boat', dur: SB.out[1] - SB.out[0], pan: [pan(280), pan(240)], gain: 0.3 },
    { t: GULL_T[0], type: 'gull', pan: 0 },
    { t: STRIKE + 0.4, type: 'splash', x: PILE.x - 13, gain: 0.35 },
    { t: HORNS[1], type: 'horn', x: 240, gain: 0.4 },
    { t: SB.back[0], type: 'boat', dur: SB.back[1] - SB.back[0], pan: [pan(240), pan(280)], gain: 0.3 },
    { t: CHIME_T, type: 'chime', x: 470, gain: 0.3 },
    { t: TUG.t0, type: 'boat', dur: TUG.t1 - TUG.t0, pan: [pan(TUG.x0), pan(TUG.x1)], gain: 0.45 },
    { t: GULL_T[1], type: 'gull', pan: 0.2 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
